import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Button,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Divider,
  Tooltip,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import TimelineIcon from '@mui/icons-material/Timeline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HistoryIcon from '@mui/icons-material/History';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import HubIcon from '@mui/icons-material/Hub';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SpeedIcon from '@mui/icons-material/Speed';
import ErrorIcon from '@mui/icons-material/Error';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import InfoIcon from '@mui/icons-material/Info';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import ScienceIcon from '@mui/icons-material/Science';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Collapse } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { formatLocalDateTime } from '../utils/date';
import authService from '../services/auth';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  transition: 'transform 0.2s, box-shadow 0.2s',
  backgroundColor: '#111827',
  border: '1px solid rgba(148, 163, 184, 0.08)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.4)',
    border: '1px solid rgba(148, 163, 184, 0.15)',
  },
}));

const StatCard = ({ title, value, icon, color, subtitle, onClick }) => (
  <StyledPaper
    elevation={2}
    onClick={onClick}
    sx={onClick ? { cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } } : {}}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box
        sx={{
          backgroundColor: `${color}15`,
          borderRadius: '50%',
          p: 1,
          mr: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" color="text.secondary">
        {title}
      </Typography>
    </Box>
    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
      {value}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        {subtitle}
      </Typography>
    )}
  </StyledPaper>
);

const AuditReportCard = ({ audit, onViewDetails, onDownloadReport }) => {
  const getScoreColor = (score) => {
    if (score >= 80) return '#388e3c';
    if (score >= 50) return '#fbc02d';
    if (score >= 30) return '#f57c00';
    return '#d32f2f';
  };

  const getScoreIcon = (score) => {
    if (score >= 80) return <CheckCircleIcon />;
    if (score >= 50) return <GppMaybeIcon />;
    if (score >= 30) return <ReportProblemIcon />;
    return <ErrorIcon />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    return formatLocalDateTime(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const handleViewFullReport = async () => {
    if (audit.reports && audit.reports.length > 0) {
      try {
        // Fetch the PDF with authentication
        const response = await fetch(`${process.env.REACT_APP_API_URL}/audit-reports/${audit.reports[0].id}/download`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          
          // Open PDF in new tab
          const newWindow = window.open(url, '_blank');
          if (newWindow) {
            newWindow.focus();
          } else {
            // Fallback: download the file
            const a = document.createElement('a');
            a.href = url;
            a.download = audit.reports[0].filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }
          
          // Clean up the blob URL after a delay
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
          }, 1000);
        } else {
          throw new Error('Failed to fetch report');
        }
      } catch (error) {
        console.error('Error opening report:', error);
        // Fallback to history page
        onViewDetails(audit);
      }
    } else {
      // If no PDF report available, navigate to history page
      onViewDetails(audit);
    }
  };

  return (
    <Card 
      sx={{ 
        mb: 2, 
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar 
            sx={{ 
              bgcolor: getScoreColor(audit.overall_score || 0),
              mr: 2,
              width: 40,
              height: 40
            }}
          >
            {getScoreIcon(audit.overall_score || 0)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" noWrap>
              {audit.firewall?.device_name || 'Unknown Device'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {audit.firewall?.type || audit.firewall?.device_name || ''}
            </Typography>
          </Box>
          <Chip
            label={`${audit.overall_score || 0}%`}
            size="small"
            sx={{ fontWeight: 'bold', backgroundColor: getScoreColor(audit.overall_score || 0), color: '#fff' }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Passed: {audit.passed_checks || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Failed: {audit.failed_checks || 0}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              Duration: {formatDuration(audit.duration)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(audit.audit_date)}
            </Typography>
          </Box>
        </Box>

        {audit.ai_model && (
          <Chip 
            label={`AI: ${audit.ai_model}`}
            size="small"
            variant="outlined"
            sx={{ mr: 1 }}
          />
        )}
        
      </CardContent>
      
      <Divider />
      
      <CardActions sx={{ justifyContent: 'flex-end', px: 2, py: 1, gap: 0.5 }}>
        {audit.reports && audit.reports.length > 0 && (
          <Tooltip title="Download Report">
            <IconButton
              size="small"
              onClick={() => onDownloadReport(audit.reports[0].id, audit.reports[0].filename)}
              color="secondary"
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={audit.reports && audit.reports.length > 0 ? 'View Report' : 'View Details'}>
          <IconButton
            size="small"
            onClick={handleViewFullReport}
            color="primary"
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

const Dashboard = ({ stats, recentActivity, auditHistory, firewalls, onAddClick, loading, error, refresh }) => {
  const navigate = useNavigate();
  const isAdmin = authService.isAdmin();

  // Audit whose report is reproduced as Figure 8 of Paper 1. Opens the stored
  // PDF in a new tab, where the browser's own viewer supplies download, print
  // and search -- an in-app viewer would duplicate all three, worse.
  const FIG8_AUDIT_ID = 879;
  const openSampleReport = async () => {
    try {
      const jwt = localStorage.getItem('access_token');
      const meta = await fetch(`${process.env.REACT_APP_API_URL}/audit-reports/by-audit/${FIG8_AUDIT_ID}`,
        { headers: { Authorization: `Bearer ${jwt}` } });
      if (!meta.ok) throw new Error(`report lookup failed (${meta.status})`);
      const { id } = await meta.json();
      const pdf = await fetch(`${process.env.REACT_APP_API_URL}/audit-reports/${id}/download`,
        { headers: { Authorization: `Bearer ${jwt}` } });
      if (!pdf.ok) throw new Error(`report download failed (${pdf.status})`);
      const url = window.URL.createObjectURL(await pdf.blob());
      window.open(url, '_blank')?.focus();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.error('Sample report could not be opened:', e);
    }
  };
  const [auditReports, setAuditReports] = useState({});
  const [showMetrics1, setShowMetrics1] = useState(() => {
    try { return localStorage.getItem('nscat_show_metrics_1') === 'true'; } catch { return false; }
  });
  const [showMetrics2, setShowMetrics2] = useState(() => {
    try { return localStorage.getItem('nscat_show_metrics_2') === 'true'; } catch { return false; }
  });
  const toggleMetrics1 = () => {
    setShowMetrics1(prev => {
      const next = !prev;
      try { localStorage.setItem('nscat_show_metrics_1', String(next)); } catch {}
      return next;
    });
  };
  const toggleMetrics2 = () => {
    setShowMetrics2(prev => {
      const next = !prev;
      try { localStorage.setItem('nscat_show_metrics_2', String(next)); } catch {}
      return next;
    });
  };
  const rm = stats?.researchMetrics || {};

  const [copySnackbar, setCopySnackbar] = useState({ open: false, message: '' });
  const [locStats, setLocStats] = useState({
    backend_loc: null,
    frontend_loc: null,
    nist_controls: null,
    utility_modules: null,
    orm_models: null,
    react_pages: null,
    max_config_length: null,
    max_concurrent_checks: null,
    audit_batch_size: null,
    audit_timeout_seconds: null,
    jwt_expiry_minutes: null,
    rag_top_k: null,
    rag_audit_time_top_k: null,
    rag_hybrid_semantic_weight: null,
    rag_hybrid_keyword_weight: null,
    rag_min_hybrid_score: null,
    dispatch_latency_max_seconds: null,
    provider_models: null,
    cdm_entity_fields: null,
    cdm_rule_fields: null,
    cdm_enums: null,
    cdm_total_fields: null,
    wazuh_rule_levels: null,
    severity_weights: null,
    rag_confidence_tiers: null,
    prompt_parts: null,
    prompt_response_fields: null,
    rag_retrieval_stats: null,
  });

  // Fetch live architecture stats once on mount so the Design Metrics output
  // stays in sync with the actual codebase (avoiding stale hard-coded numbers).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/system/architecture`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setLocStats(data);
        }
      } catch (e) {
        // Silent: dashboard falls back to '—' or last-known value if unreachable.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Shared helpers for both copy-text builders.
  const fmt = (v, suffix = '') => (v === null || v === undefined ? '—' : `${v}${suffix}`);
  const fmtN = (v) => (v == null ? '—' : Number(v).toLocaleString());
  const fmtPct = (v, digits = 2) => (v == null ? '—' : `${Number(v).toFixed(digits)}%`);
  const fmtSec = (v, digits = 2) => (v == null ? '—' : `${Number(v).toFixed(digits)}s`);

  // Common preamble: header + infrastructure + audits + severity tier counts.
  // Both Paper 1 and Paper 2 copy buttons need this context.
  const buildCommonPreamble = () => {
    const lines = [];
    // Format "Captured" in PKT (Asia/Karachi) to match the top banner clock.
    const capturedPkt = (() => {
      try {
        const fmt = new Intl.DateTimeFormat('en-US', {
          timeZone: 'Asia/Karachi',
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
          hour12: false,
        });
        const p = fmt.formatToParts(new Date());
        const get = (t) => p.find(x => x.type === t)?.value || '';
        let hh = get('hour');
        if (hh === '24') hh = '00';
        return `${get('day')}/${get('month')}/${get('year')}, ${hh}:${get('minute')}:${get('second')} PKT`;
      } catch {
        return new Date().toISOString();
      }
    })();
    lines.push(`Captured: ${capturedPkt}`);
    if (lastAuditDate) lines.push(`Last audit: ${formatLocalDateTime(lastAuditDate)}`);
    lines.push('');
    lines.push('=== INFRASTRUCTURE ===');
    lines.push(`Devices: ${stats?.totalFirewalls ?? 0}`);
    lines.push(`Device Vendors: ${stats?.totalTypes ?? 0}`);
    lines.push(`AI Vendors: ${stats?.aiVendors ?? 0}`);
    // Local/Paid/Free LLM counts intentionally omitted: those reflect the live
    // LLM-management UI registration state (current configuration), which can
    // drift from the audit_history database. The DB-derived 'Distinct LLM
    // Models Used' (in Headline Validation below) is the authoritative count
    // for the 36-models claim in Paper 1.
    lines.push('');
    lines.push('=== AUDITS ===');
    lines.push(`Total Audits: ${stats?.directAudits ?? 0}`);
    lines.push(`Average Score: ${Math.round(stats?.averageScore || 0)}%`);
    lines.push(`Critical: ${stats?.criticalAlerts ?? 0}`);
    lines.push(`High: ${stats?.highAlerts ?? 0}`);
    lines.push(`Medium: ${stats?.mediumAlerts ?? 0}`);
    lines.push(`Low: ${stats?.lowAlerts ?? 0}`);
    lines.push(`Compliant: ${stats?.compliantAlerts ?? 0}`);
    lines.push('');
    return lines;
  };

  // Paper 1 (Design) — headline DB-backed validation + all code-backed
  // architectural constants. Suitable for pasting into an AI prompt to update
  // the design paper.
  const buildMetrics1Text = () => {
    const lines = [];
    lines.push('NSCAT DASHBOARD SNAPSHOT — PAPER 1 (DESIGN)');
    lines.push(...buildCommonPreamble());

    lines.push('=== HEADLINE VALIDATION (DB-backed) ===');
    lines.push(`Total Findings: ${fmtN(rm.total_findings)}`);
    lines.push(`PDF Success: ${fmtPct(rm.pdf_success_rate)} (${rm.pdf_generated ?? 0}/${rm.pdf_total ?? 0})`);
    lines.push(`Syslog Delivery: ${rm.syslog_success_rate != null ? rm.syslog_success_rate.toFixed(0) + '%' : '—'} (${rm.syslog_success ?? 0}/${rm.syslog_total ?? 0})`);
    lines.push(`SOAR Delivery: ${rm.soar_success_rate != null ? rm.soar_success_rate.toFixed(1) + '%' : '—'} (${rm.soar_success ?? 0}/${rm.soar_total ?? 0})`);
    lines.push(`Distinct LLM Models Used: ${fmt(rm.distinct_models)}`);
    lines.push(`Extraction Failures: ${fmt(rm.extraction_failures)}`);
    lines.push(`Max Config Size: ${rm.max_config_chars != null ? rm.max_config_chars.toLocaleString() + ' chars' : '—'}`);
    // Headline empirical claims matching the Paper 1 abstract.
    {
      const strict  = locStats.rag_top1_strict_pct;
      const lenient = locStats.rag_top1_lenient_pct;
      const gtN     = locStats.rag_ground_truth_sample_size;
      lines.push(`RAG Top-1 Mapping Accuracy: ${strict != null ? strict.toFixed(0) + '%' : '—'} strict / ${lenient != null ? lenient.toFixed(0) + '%' : '—'} lenient (n=${fmt(gtN)} annotated ground truth)`);
    }
    {
      const v = locStats.verified_share_pct;
      const ai = locStats.ai_inferred_share_pct;
      const n = locStats.verified_ai_inferred_sample_size;
      lines.push(`Verified vs AI-inferred Split: ${v != null ? v.toFixed(1) + '%' : '—'} Verified / ${ai != null ? ai.toFixed(1) + '%' : '—'} AI-inferred (n=${fmt(n)} post-deployment findings)`);
    }
    {
      const sd = locStats.score_variability_std_dev;
      const nr = locStats.score_variability_n_runs;
      const fw = locStats.score_variability_firewall;
      const llm = locStats.score_variability_llm;
      lines.push(`Score Variability (std dev): ${sd != null ? sd.toFixed(2) + ' pts on 0-100 scale' : '—'} across ${fmt(nr)} repeated runs of ${fw ?? '—'} on ${llm ?? '—'} (config unchanged)`);
    }
    lines.push('');

    const meta1 = rm._meta || {};
    lines.push('=== GROUND-TRUTH DATA FRESHNESS ===');
    if (meta1.three_way_mapping_mtime || meta1.three_way_cache_status === 'missing') {
      const suffix = statusSuffix(meta1.three_way_cache_status, meta1.three_way_mapping_mtime, {
        coverage: meta1.three_way_gold_n != null ? `n=${meta1.three_way_gold_n} gold-standard findings` : null,
        refresh_cmd: 'scripts/compute_three_way_mapping.py',
      });
      lines.push(`Three-way mapping    : ${fmtTs(meta1.three_way_mapping_mtime) || '—'}${suffix}`);
    }
    if (meta1.ground_truth_mtime) {
      const nLabel = meta1.ground_truth_n != null ? ` (n=${meta1.ground_truth_n})` : '';
      lines.push(`Gold-standard data   : ${fmtTs(meta1.ground_truth_mtime)}${nLabel} — ragGroundTruth.json`);
    }
    lines.push('');

    // ── Paper 1 ground-truth mapping precision (moved from the evaluation payload) ──
    if (rm.three_way_mapping) {
      const tw_ts = fmtTs((rm._meta || {}).three_way_mapping_mtime);
      lines.push(`=== THREE-WAY NIST MAPPING (gold-standard n=${rm.three_way_mapping.n ?? 100})${tw_ts ? ' [cache: ' + tw_ts + ']' : ''} ===`);
      const fmtStrat = (label, v) => {
        if (!v) return null;
        const noneStr = v.none ? `, ∅=${v.none}` : '';
        return `${label.padEnd(22)}: c=${v.c}, p=${v.p}, w=${v.w}${noneStr}; strict ${v.strict_pct?.toFixed(1)}%, lenient ${v.lenient_pct?.toFixed(1)}%`;
      };
      [
        ['Keyword Categorical', rm.three_way_mapping.keyword_categorical],
        ['RAG Hybrid (prod)', rm.three_way_mapping.rag_hybrid],
        ['LLM-Native Citation', rm.three_way_mapping.llm_native_citation],
      ].forEach(([l, v]) => {
        const line = fmtStrat(l, v);
        if (line) lines.push(line);
      });
      lines.push('');
    }

    if (rm.rag_family_accuracy && Array.isArray(rm.rag_family_accuracy.family_stats) && rm.rag_family_accuracy.family_stats.length) {
      const gt_ts = fmtTs((rm._meta || {}).ground_truth_mtime);
      lines.push(`=== RAG-HYBRID PER-FAMILY TOP-1 ACCURACY (gold-standard n=100)${gt_ts ? ' [annotation file: ' + gt_ts + ']' : ''} ===`);
      lines.push('Family |   n |  c |  p |  w | Strict | Lenient');
      rm.rag_family_accuracy.family_stats.forEach(f => {
        lines.push(`${(f.family || '').padEnd(6)} | ${String(f.n).padStart(3)} | ${String(f.c).padStart(2)} | ${String(f.p).padStart(2)} | ${String(f.w).padStart(2)} | ${f.strict_pct.toFixed(1).padStart(5)}% | ${f.lenient_pct.toFixed(1).padStart(5)}%`);
      });
      if (rm.rag_family_accuracy.headline) {
        const h = rm.rag_family_accuracy.headline;
        lines.push(`All    | ${String(h.n).padStart(3)} | ${String(h.c).padStart(2)} | ${String(h.p).padStart(2)} | ${String(h.w).padStart(2)} | ${h.strict_pct.toFixed(1).padStart(5)}% | ${h.lenient_pct.toFixed(1).padStart(5)}%`);
      }
      lines.push('');
    }

    lines.push('=== ARCHITECTURAL CONSTANTS (Code-backed) ===');
    lines.push(`NIST SP 800-53 Controls: ${fmtN(locStats.nist_controls)} (pre-embedded in ChromaDB)`);
    {
      const cdm = locStats;
      lines.push(`CDM Fields: ${fmt(cdm.cdm_entity_fields)} entity + ${fmt(cdm.cdm_rule_fields)} rule + ${fmt(cdm.cdm_enums)} enums = ${fmt(cdm.cdm_total_fields)}`);
    }
    lines.push('CDM Coverage: 20/22 (all 6 vendor parsers)');
    lines.push('Embedding Model: mxbai-embed-large (1024-d)');
    lines.push(`Hybrid RAG Score: ${locStats.rag_hybrid_semantic_weight != null ? Math.round(locStats.rag_hybrid_semantic_weight * 100) : '—'}/${locStats.rag_hybrid_keyword_weight != null ? Math.round(locStats.rag_hybrid_keyword_weight * 100) : '—'} (semantic / keyword)`);
    {
      const sw = locStats.severity_weights;
      // Paper 1 reports the weights as positive values (10, 8, 5, 3) applied
      // as deductions from 100; the backend stores them signed for direct
      // addition. Display the absolute value with an explicit "deduction"
      // label so the metric line matches the paper's wording verbatim.
      const fmtw = (k) => (sw && sw[k] != null ? Math.abs(sw[k]) : '—');
      lines.push(`Severity Weights: ${fmtw('critical')} / ${fmtw('high')} / ${fmtw('medium')} / ${fmtw('low')} (Critical / High / Medium / Low; deducted from 100)`);
    }
    lines.push(`MAX_CONFIG_LENGTH: ${fmtN(locStats.max_config_length)} chars (total ingested; per-Part 5 prompt cap is 3,000 chars)`);
    lines.push(`MAX_CONCURRENT_CHECKS: ${fmt(locStats.max_concurrent_checks)}`);
    lines.push('FW Vendors / LLM Providers: 6 / 6');
    // Per-Provider Models intentionally omitted from the copy output:
    // this comes from the live LLM-management UI state (current configuration),
    // which can drift from the audit_history database used elsewhere in this
    // report. Distinct LLM Models Used (above, DB-derived) is the authoritative
    // count for the 36-models claim in Paper 1.
    lines.push(`Backend LOC: ${fmtN(locStats.backend_loc)} (Python)`);
    lines.push(`Frontend LOC: ${fmtN(locStats.frontend_loc)} (React)`);
    lines.push(`Utility Modules: ${fmt(locStats.utility_modules)}`);
    lines.push(`PostgreSQL ORM Models: ${fmt(locStats.orm_models)}`);
    lines.push(`React Page Components: ${fmt(locStats.react_pages)}`);
    lines.push('Tokens per Char: 0.25');
    lines.push('Chunk Trigger: 50% of model context window');
    lines.push(`RAG Cutoff (min hybrid score): ${locStats.rag_min_hybrid_score != null ? locStats.rag_min_hybrid_score.toFixed(2) : '—'}`);
    lines.push(`RAG Retrieval: top-${fmt(locStats.rag_top_k)} (chatbot/finding-mapping); audit-time top-${fmt(locStats.rag_audit_time_top_k)} (Path A prompt enrichment)`);
    lines.push(`Audit Batch Size: ${fmt(locStats.audit_batch_size)}`);
    lines.push(`Global Audit Timeout: ${locStats.audit_timeout_seconds != null ? locStats.audit_timeout_seconds.toLocaleString() + 's (' + Math.round(locStats.audit_timeout_seconds / 60) + ' min)' : '—'}`);
    lines.push(`JWT Expiry: ${locStats.jwt_expiry_minutes != null ? Math.round(locStats.jwt_expiry_minutes / 60) + 'h' : '—'}`);
    lines.push('Sections per Config: 10-17 (~3,000 chars each)');
    {
      const wl = locStats.wazuh_rule_levels;
      const w = (k) => (wl && wl[k] != null ? wl[k] : '—');
      lines.push(`Wazuh Rule Levels: ${w('critical')} / ${w('high')} / ${w('medium')} / ${w('low')} / ${w('compliant')} (Critical / High / Medium / Low / Compliant)`);
    }
    lines.push('SSH Read Timeouts: 30s Juniper / 120s Huawei / 180s Fortinet (10s default; 30s Cisco FTD for connection/banner/auth)');
    lines.push('Score Extraction Cascade: 4 tiers (L1 structured / L2 JSON parse / L3 5 regex / L4 severity deduction)');
    {
      const t = locStats.rag_confidence_tiers;
      const hi = t && t.high_min != null ? t.high_min.toFixed(3) : '—';
      const md = t && t.medium_min != null ? t.medium_min.toFixed(3) : '—';
      lines.push(`RAG Confidence Tiers: High >= ${hi}, Medium >= ${md}, Low < ${md} hybrid score`);
    }
    lines.push('SOAR Retry: 3 attempts with 2^n s exponential backoff (2s, 4s, 8s)');
    {
      const med = locStats.audit_latency_median_seconds;
      const p95 = locStats.audit_latency_p95_seconds;
      const p99 = locStats.audit_latency_p99_seconds;
      const n   = locStats.audit_latency_sample_size;
      const p95Min = (p95 != null) ? Math.round(p95 / 60) : null;
      const p99Min = (p99 != null) ? Math.round(p99 / 60) : null;
      const medStr = med != null ? `${med}s` : '—';
      const p95Str = p95 != null ? `${p95}s${p95Min ? ` (~${p95Min} min)` : ''}` : '—';
      const p99Str = p99 != null ? `${p99}s${p99Min ? ` (~${p99Min} min)` : ''}` : '—';
      lines.push(`Audit-to-Notification Latency: median ${medStr}, p95 ${p95Str}, p99 ${p99Str} (n=${fmt(n)}; measured from audit_history.duration)`);
    }
    lines.push(`Industry Baseline (NIST SP 800-40 Rev 4 mitigation latency): ${fmt(locStats.industry_baseline_days_min)}--${fmt(locStats.industry_baseline_days_max)} days`);
    lines.push('SOAR Email Templates: 4 (Critical / High / Medium / Low; Compliant not emailed)');
    lines.push('Tier SLAs: Critical 24h, High 48h, Medium 72h, Low 1 week, Compliant not emailed');
    lines.push(`Prompt Parts: ${fmt(locStats.prompt_parts)} (header, check ID, NIST mapping, device context, config text, vendor hints, task, NIST guide, JSON schema)`);
    lines.push(`JSON Response Fields: ${fmt(locStats.prompt_response_fields)} (status, nist_sp_800_53, summary, issue, fix, verify, risk_level)`);
    lines.push('Test Configurations: 9 (1-2 per vendor, 30-80 rules)');

    // RAG retrieval-quality statistics (retrieval confidence — NOT accuracy)
    const rs = locStats.rag_retrieval_stats;
    if (rs) {
      lines.push('');
      lines.push('=== RAG RETRIEVAL QUALITY (computed from completed audits) ===');
      lines.push(`Findings analysed: ${fmtN(rs.findings_total)} across ${fmt(rs.audits_with_findings)} audits (${fmtN(rs.rag_hits)} produced a RAG hit)`);
      lines.push(`Hybrid Score: median ${rs.hybrid_score_median != null ? rs.hybrid_score_median.toFixed(3) : '—'}, p25 ${rs.hybrid_score_p25 != null ? rs.hybrid_score_p25.toFixed(3) : '—'}, p75 ${rs.hybrid_score_p75 != null ? rs.hybrid_score_p75.toFixed(3) : '—'}`);
      lines.push(`Confidence Tier Mix: ${rs.pct_high != null ? rs.pct_high.toFixed(1) : '—'}% High (>=0.515), ${rs.pct_medium != null ? rs.pct_medium.toFixed(1) : '—'}% Medium (>=0.418), ${rs.pct_low != null ? rs.pct_low.toFixed(1) : '—'}% Low (<0.418)`);
      lines.push(`Below 0.30 cutoff: ${rs.pct_below_cutoff != null ? rs.pct_below_cutoff.toFixed(1) : '—'}%`);
      if (Array.isArray(rs.top_families) && rs.top_families.length) {
        const fam = rs.top_families.slice(0, 6).map(t => `${t.family} ${t.count}`).join(', ');
        lines.push(`Top NIST Families: ${fam}`);
      }
      lines.push('Note: retrieval-confidence signals; not mapping correctness (see §VIII-B L5).');
    }

    return lines.join('\n');
  };

  // Paper 2 (Evaluation) — statistical/distributional metrics for the
  // empirical evaluation paper.
  // Format a Unix-epoch seconds value to a human-readable local timestamp;
  // returns null if missing. Used for "Last refreshed" captions on each
  // metrics section so reviewers can see when cache-backed data was last
  // regenerated (DB-backed sources are live and use the page-load time).
  const fmtTs = (epochSec) => {
    if (!epochSec) return null;
    try {
      return new Date(epochSec * 1000).toLocaleString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      });
    } catch { return null; }
  };

  // Age in days from a Unix-epoch seconds; null if missing.
  const ageDays = (epochSec) => {
    if (!epochSec) return null;
    return Math.floor((Date.now() / 1000 - epochSec) / 86400);
  };

  // Cache freshness suffix for the copy-to-clipboard metric texts. Defined at
  // component scope because both buildMetrics1Text (Paper 1) and
  // buildMetrics2Text (Paper 2) emit freshness lines.
  const statusSuffix = (status, mtimeEpoch, extras = {}) => {
    if (status === 'current') {
      const cov = extras.coverage ? ` (${extras.coverage})` : '';
      return ` — current${cov}`;
    }
    if (status === 'outdated') {
      const reason = extras.audits_added != null && extras.audits_added > 0
        ? ` — outdated (${extras.audits_added} new audit${extras.audits_added === 1 ? '' : 's'} since cache)`
        : ' — outdated (regenerate)';
      return reason;
    }
    if (status === 'missing') return ` — missing (${extras.refresh_cmd || 'regenerate'})`;
    // Fallback: age-based hint when status not provided
    if (mtimeEpoch) {
      const d = ageDays(mtimeEpoch);
      return d != null ? ` (${d} day${d === 1 ? '' : 's'} old)` : '';
    }
    return '';
  };

  // Render a status-aware cache freshness label. Prefers the backend-provided
  // status ("current" / "outdated" / "missing") which compares cache content to
  // current DB state. Falls back to age-based hint when status is absent (older
  // backend builds). Returns { color, text } for use with MUI Chip.
  const cacheChip = (status, mtimeEpoch, extras = {}) => {
    if (status === 'missing') {
      const refresh = extras.refresh_cmd ? ` — run ${extras.refresh_cmd}` : '';
      return { color: 'error', text: `Cache absent${refresh}` };
    }
    if (status === 'outdated') {
      const reason = extras.audits_added_since != null && extras.audits_added_since > 0
        ? `${extras.audits_added_since} new audit${extras.audits_added_since === 1 ? '' : 's'} — regenerate`
        : 'Cache outdated — regenerate';
      return { color: 'warning', text: reason };
    }
    if (status === 'current') {
      const coverage = extras.coverage_label ? ` (${extras.coverage_label})` : '';
      const stamp = fmtTs(mtimeEpoch);
      return { color: 'success', text: `Current${coverage}${stamp ? ' · ' + stamp : ''}` };
    }
    if (status === 'frozen') {
      const coverage = extras.coverage_label ? ` (${extras.coverage_label})` : '';
      return { color: 'info', text: `Frozen artifact${coverage}` };
    }
    // Fallback: no status field — use mtime age as before.
    const stamp = fmtTs(mtimeEpoch);
    if (!stamp) return { color: 'default', text: 'Cache: —' };
    return { color: ageDays(mtimeEpoch) > 7 ? 'warning' : 'default', text: `Cache: ${stamp}` };
  };

  // Metric-level tooltip descriptions. Keyed by a stable slug per metric; the UI
  // wraps each metric label/value with <Tooltip arrow title={METRIC_TOOLTIPS[key]}>.
  // Descriptions are practitioner-facing (what the number means, how to read it,
  // and the Paper 2 provenance where applicable) rather than mathematical
  // definitions.
  const METRIC_TOOLTIPS = {
    // ── Corpus Compliance Score Distribution (Paper 2 Table III "All" row) ──
    'compliance.mean':   'Arithmetic mean of overall_score across every scored audit (unweighted per-audit). Paper 2 Table III headline: 39.0 with no fully-compliant verdicts (F1 finding).',
    'compliance.median': '50th percentile of the compliance-score distribution. Median = mean = 39 indicates a roughly symmetric distribution around the midpoint on a 0–100 scale.',
    'compliance.sd':     'Population standard deviation of compliance scores across all scored audits. High SD (32.9) reflects the wide provider spread reported in Paper 2 §IV-A.',
    'compliance.range':  'Corpus min–max on the 0–100 scale. Max of 90 was reached by five audits (Ollama and OpenAI models); no audit reached full compliance.',

    // ── Alert Classification (Paper 2 abstract F5, §IV) ──
    'alert.critical':  'Audits classified as Critical severity (score < 30). Paper 2 F1 finding: 131/303 (43.2%) — the modal severity tier.',
    'alert.high':      'Audits classified as High severity (score 30–59). 31/303 (10.2%) in the current corpus.',
    'alert.medium':    'Audits classified as Medium severity (score 60–89). 105/303 (34.6%) in the current corpus.',
    'alert.low':       'Audits classified as Low severity (score 90–95). 36/303 (11.9%) in the current corpus.',
    'alert.compliant': 'Audits classified as fully Compliant (score ≥ 96). Zero in the current corpus — the F1 "no fully-compliant verdicts" claim.',

    // ── Score-Extraction Cascade Tier Distribution (Paper 2 §IV-D, B5 contribution) ──
    'cascade.T0': 'Tier 0 — extraction failed on all four cascade levels; audit is dropped from scored population. Paper 2 §IV-D: 7/51 instrumented audits (13.7%).',
    'cascade.T1': 'Tier 1 — clean structured compliance_score field returned by the LLM (no forgiveness needed). 23/51 (45.1%) on instrumented sample.',
    'cascade.T2': 'Tier 2 — JSON re-parse of the raw text (unwrap code fences, retry parser). Zero fires in the instrumented sample.',
    'cascade.T3': 'Tier 3 — regex extraction for prose-embedded scores. Zero fires; the cascade is effectively bimodal (T1 or T4).',
    'cascade.T4': 'Tier 4 — severity-deduction fallback: reconstructs a numeric score from finding severities using -10/-8/-5/-3 weights. 21/51 (41.2%) — the operational safety net.',

    // ── RAG Retrieval Health (Paper 2 §V, lines 733-735) ──
    'rag.coverage':      'Share of findings that received a top-5 retrieval above the 0.30 confidence cutoff. 99.96% (2,777/2,778); the one finding below cutoff returned no control and was left unmapped — the RAG path has no keyword-table fallback.',
    'rag.median':        'Median hybrid score H = 0.7·S_sem + 0.3·S_kw on the top-1 candidate across all findings. Paper 2 median 0.473 — the retriever rarely produces very-high-confidence matches.',
    'rag.iqr':           'Interquartile range of hybrid scores. Width just 0.094 on a 0–1 scale — tight mid-band clustering that explains the low exact-control agreement in §V-A.',
    'rag.min_max':       'Extremes of the hybrid-score distribution across all top-1 candidates.',
    'rag.band_high':     'Share of findings whose top-1 hybrid score H ≥ 0.515 (high-confidence retrieval band; p75 of the observed distribution).',
    'rag.band_medium':   'Share of findings whose top-1 hybrid score is in [0.418, 0.515) — the dominant band (65.1%).',
    'rag.band_low':      'Share of findings whose top-1 hybrid score is below 0.418 (p25) — retained above the 0.30 cutoff but marginal.',
    'rag.band_below':    'Share of findings that fell below the 0.30 cutoff. Exactly one finding (0.04%) in the current corpus.',
    'rag.weighting':     'Hybrid RAG weighting: semantic similarity (70%) + keyword overlap (30%). ChromaDB with Ollama mxbai-embed-large embeddings.',
    'rag.cutoff':        'Confidence threshold τ = 0.30 below which candidates are dropped before top-5 re-ranking. Suppresses weak mappings that would degrade traceability.',
    'rag.topk':          'Number of top NIST controls retrieved per finding from ChromaDB (k = 5).',
    'rag.corpus':        'NIST SP 800-53 Rev 5 catalogue indexed in ChromaDB: 1,014 controls total (300 base controls + 714 enhancements).',

    // ── Inter-Strategy Agreement (Paper 2 §V-A) ──
    'isa.krippendorff':  "Krippendorff's α on nominal-scale family codings across three raters (S1 keyword, S2 RAG, S3 LLM-native). 0.205 falls in the slight-to-fair band (<0.40) — strategies make genuinely different choices.",
    'isa.fleiss':        "Fleiss' κ on family codings, computed on items where all three strategies emit a prediction. 0.221 corroborates the low-agreement finding.",
    'isa.s1_cov':        'Strategy 1 (keyword categorical, 43-pattern + 75-word stoplist + SC-07 fallback) emits a NIST family for 100% of findings.',
    'isa.s2_cov':        'Strategy 2 (production RAG hybrid, τ = 0.30, top-k = 5, 70/30 weighting) emits a NIST family for 100% of findings.',
    'isa.s3_cov':        'Strategy 3 (LLM-native citation, parsed from LLM Context and Recommendation fields) emits a NIST family for only 13.6% of findings — cannot stand alone as a mapping pathway.',
    'isa.tw_agree':      'Findings on which all three strategies emit a prediction AND all three agree on family. 108/2,967 = 3.6% — the strongest non-trivial signal in the dataset.',
    'isa.tw_partial':    'Findings on which exactly two strategies agree on family (or S3 abstains). The heterogeneous bulk of the corpus.',
    'isa.tw_disagree':   'Findings on which all three strategies emit a prediction AND the three families are distinct. 81/2,967 = 2.7% — the strict subset analysed in §V-B.',
    'isa.entropy_s1':    'Shannon entropy (nats) of S1 family distribution. 1.06 — lowest of the three, driven by SC-07 fallback concentration.',
    'isa.entropy_s2':    'Shannon entropy (nats) of S2 family distribution. 1.61 — RAG spreads more broadly across families than the keyword table.',
    'isa.entropy_s3':    'Shannon entropy (nats) of S3 family distribution over its 404-finding coverage subset. 1.79 — highest, but restricted to S3-emitting findings.',
    'isa.sc_conc':       'Share of S1 predictions that map to the SC (System and Communications Protection) family. 68.0% — direct consequence of the SC-07 keyword-table fallback.',
    'isa.pair_s1s2':     'Pairwise agreement between Strategy 1 and Strategy 2. Family-level 53.1%, exact-control 5.2% — strategies disagree on the exact control for 94.8% of findings.',
    'isa.pair_s1s3':     'Pairwise agreement between Strategy 1 and Strategy 3, over the 404 findings where S3 emits. Family 41.8%, exact 9.2%.',
    'isa.pair_s2s3':     'Pairwise agreement between Strategy 2 and Strategy 3, over the 404 findings where S3 emits. Family 40.4%, exact 8.9%.',

    // ── Defence-demo metrics (Paper 2 §IV-A, §IV-B, §VI-A, §VII-D) ──
    'dm.drift':  'Within-session vs across-session score dispersion, on repeat pairs whose mean exceeds 50 (removing the scoring-floor effect). Same-day repeats vary by ~2%; repeats a day or more apart by ~35%. The instability is drift between sessions, not sampling noise within them.',
    'dm.floor':  'F5 decomposition. The 16.9-point vendor gap is carried entirely by how often the score reaches its lower bound: Check Point and Huawei hit zero in 47.3% of audits against 16.7% for the other four. Conditional on a non-zero score the groups differ by 0.07 points.',
    'dm.corpus': 'Per-configuration audit counts. Two configurations carry 62% of the corpus and three vendors have a single configuration each, so vendor and configuration are not separable for those platforms.',
    'dm.seeded': 'Seeded-weakening detection. Weakenings verifiable directly from configuration text (e.g. strong-encryption-disable, admin-telnet enable); measures whether each audit mentions them. 51.3% of 446 opportunities. Bounds attention, not correctness.',

    // ── Consensus Diagnostics (Paper 2 §IV-B) ──
    'cd.icc':      'Intraclass Correlation Coefficient ICC(2,1), two-way random absolute-agreement, on the first k=2 repeats per (firewall_id, ai_model) pair. 0.739 = excellent (Cicchetti 1994 bands).',
    'cd.mech_a':   'Mechanism A (majority-vote at PASS/FAIL boundary at score 60): pair-coverage rate = fraction of consensus-eligible pairs that survive rejection. 51.3%.',
    'cd.mech_b':   'Mechanism B (tier-weighted mean, paid=1.0 / free=0.7 / local=0.5): coverage rate flags pairs where weighted vs simple mean differs > 5pp or flips verdict. 38.5%.',
    'cd.mech_c':   'Mechanism C (variance threshold, SD > 15pp or CV > 30% on 0–100 scale): coverage rate = fraction of pairs surviving variance-threshold rejection. 38.5%.',

    // ── Failure-Mode Taxonomy (Paper 2 §V-B) ──
    'fm.domain_bleed':      'Domain Bleed — one strategy maps a network/firewall finding into a family whose scope is plainly elsewhere (PE, MP, MA, AT, IR on network findings). 28/81 = 34.6% of strict-3-way cases.',
    'fm.fallback':          'Fallback Dominance — S1 SC-07 or SI-04 anchor with no lexical anchor in the underlying finding text. 26/81 = 32.1% — SC-07 acts as a default attractor.',
    'fm.vocab':             'Vocabulary Divergence — surface lexical cues (logging, encryption, filtering) pull different strategies toward different controls (AU-12, SC-13, SC-07(11)). 17/81 = 21.0%.',
    'fm.family_adj':        'Family Adjacency — near-miss confusions between related families (SI-04 vs SC-07, IA-02 vs AC-03). 9/81 = 11.1%.',
    'fm.vendor':            'Vendor-Specific Terminology Trap — vendor lexicon (Check Point / Juniper set-syntax) misleads mapping. Fires once (1.2%); may collapse into Vocabulary Divergence.',
    'fm.enhancement':       'Enhancement-Base Confusion — SC-07 base vs SC-07(11) enhancement, or other sub-family drift. Zero fires on this subset (taxonomy-coverage open question).',
    'fm.conf_high':         'High-confidence taxonomy labels (58/81 = 71.6%). Cases with clear pattern match to a single category.',
    'fm.conf_med':          'Medium-confidence labels (23/81 = 28.4%). Concentrated in Vocabulary Divergence and ambiguous Domain Bleed instances.',
    'fm.conf_low':          'Low-confidence labels. Zero cases — the taxonomy resolves every strict-3-way case at high or medium confidence.',
    'fm.adversarial':       'Second-pass adversarial blind review on a 20-case stratified sub-sample of the strict 81-case set. 90% inter-classifier agreement (18/20) as a rule-application consistency check.',
    'fm.loose_100':         'Looser 100-case set (≥ 2 strategies emit + distinct families). Under the precedence rule, 100/100 label as LLM-Citation Gap — the corpus-scale gating failure mode.',

    // ── Three-Way NIST Mapping (Paper 2 Table IX) ──
    'twm.kw':      'Strategy 1 top-1 accuracy on the 100-finding hand-annotated gold standard. Strict (c/n) = 30.0%; Lenient (c+0.5p)/n = 46.0%. Simpler keyword strategy beats production RAG on gold-standard mapping (F2).',
    'twm.rag':     'Strategy 2 (production RAG hybrid) top-1 accuracy on the 100-finding gold standard. Strict 27.0% / Lenient 39.0%.',
    'twm.llm':     'Strategy 3 (LLM-native citation) top-1 accuracy on the 100-finding gold standard. Strict 7.0% / Lenient 16.0%; abstains on 60 findings.',

    // ── Per-Provider & Per-Vendor tables (column-level) ──
    'col.provider':  'LLM provider (Ollama local, OpenAI, Anthropic Claude, Google AI Studio, Groq, OpenRouter).',
    'col.vendor':    'Firewall vendor / platform (Cisco FTD, Palo Alto PAN-OS, Juniper SRX, Fortinet FortiGate, Huawei USG, Check Point).',
    'col.n':         'Audit count for this row within the 303-audit corpus.',
    'col.mdl':       'Distinct ai_model identifiers observed for this provider (base model + variant).',
    'col.mean':      'Arithmetic mean of compliance scores for this row.',
    'col.med':       'Median compliance score for this row.',
    'col.sd':        'Standard deviation of compliance scores for this row.',
    'col.iqr':       'Interquartile range (25th–75th percentile) of compliance scores, rounded to integers.',
    'col.p50s':      'Median wall-clock audit duration in seconds.',
    'col.p99s':      '99th-percentile wall-clock audit duration in seconds — the tail latency operators must plan for.',

    // ── Statistical / Evaluation Metrics headline row ──
    'metrics.score_cv':        'Median coefficient of variation (SD/mean × 100) computed within each (model, firewall) pair with ≥ 2 audits, then aggregated. 34.2% across 36 pairs — rules out single-shot verdicts.',
    'metrics.parser_gap':      'F5 vendor gap (Paper 2 §VI-A): Check Point and Huawei average 16.9 points below the other four platforms. This is NOT a parser artefact — all six vendors have dedicated parsers with equivalent field coverage and the generic fallback is never exercised in this corpus. The gap is carried by how often the score reaches its floor; see the floor-censoring decomposition below.',
    'metrics.audit_duration':  'Per-audit wall-clock time from dispatch to result. Paper 2 §IV-F: median 21s, p99 635s. Right-skewed by Ollama slow paths.',
    'metrics.llm_wallclock':   'Cumulative LLM-call wall-clock time summed across every audit in the corpus.',
    'metrics.non_llm_lat':     'Latency excluding LLM inference (parsing, DB writes, PDF generation, syslog dispatch). Median 0.01s indicates I/O is not the bottleneck.',
    'metrics.dispatch_lat':    'Time from audit-request receipt to first LLM call dispatch (config parsing + prompt assembly + queue wait).',
    'metrics.extraction_rec':  'Fraction of instrumented audits where the score was recovered by Tier 2–4 cascade fallback rather than Tier 1 clean extraction. 41.2% — Tier 4 severity-deduction is doing operational work.',

    // ── Infrastructure ──
    'infra.devices':    'Firewall devices registered in the credential store.',
    'infra.fw_vendors': 'Distinct firewall vendor platforms represented among registered devices.',
    'infra.ai_vendors': 'LLM providers with at least one configured model in the LLM catalogue.',
    'infra.total':      'Total audit runs across the corpus (all statuses).',
    'infra.avg_score':  'Corpus-mean overall compliance score across scored audits.',
  };

  const buildMetrics2Text = () => {
    const lines = [];
    lines.push('NSCAT DASHBOARD SNAPSHOT — PAPER 2 (EVALUATION)');
    lines.push(...buildCommonPreamble());

    // Data-source freshness summary — content-aware status from backend
    const meta = rm._meta || {};

    lines.push('=== DATA-SOURCE FRESHNESS ===');
    lines.push(`DB live              : fetched ${fmtTs(meta.fetched_at) || 'now'}`);
    if (meta.rag_cache_mtime || meta.rag_cache_status === 'missing') {
      const suffix = statusSuffix(meta.rag_cache_status, meta.rag_cache_mtime, {
        coverage: meta.rag_cache_audits_walked != null
          ? `${meta.rag_cache_audits_walked} audits, ${meta.rag_cache_findings_total ?? '?'} findings`
          : null,
        audits_added: meta.audits_added_since_rag_cache,
        refresh_cmd: 'scripts/compute_rag_metrics.py',
      });
      lines.push(`RAG metrics cache    : ${fmtTs(meta.rag_cache_mtime) || '—'}${suffix}`);
    }
    if (meta.inter_strategy_mtime || meta.inter_strategy_cache_status === 'missing') {
      const suffix = statusSuffix(meta.inter_strategy_cache_status, meta.inter_strategy_mtime, {
        coverage: meta.inter_strategy_audits_at_generation != null
          ? `${meta.inter_strategy_audits_at_generation} audits at gen`
          : null,
        audits_added: meta.inter_strategy_audits_added_since,
        refresh_cmd: 'scripts/compute_inter_strategy_agreement.py',
      });
      lines.push(`Inter-strat agreement: ${fmtTs(meta.inter_strategy_mtime) || '—'}${suffix}`);
    }
    if (meta.consensus_diagnostics_mtime || meta.consensus_diagnostics_cache_status === 'missing') {
      const suffix = statusSuffix(meta.consensus_diagnostics_cache_status, meta.consensus_diagnostics_mtime, {
        coverage: meta.consensus_diagnostics_audits_at_generation != null
          ? `${meta.consensus_diagnostics_audits_at_generation} audits at gen`
          : null,
        audits_added: meta.consensus_diagnostics_audits_added_since,
        refresh_cmd: 'scripts/compute_consensus_diagnostics.py',
      });
      lines.push(`Consensus diagnostics: ${fmtTs(meta.consensus_diagnostics_mtime) || '—'}${suffix}`);
    }
    if (meta.failure_mode_mtime || meta.failure_mode_cache_status === 'missing') {
      let suffix;
      if (meta.failure_mode_cache_status === 'frozen') {
        suffix = ' — frozen artifact (Paper 2 §V-B; qualitative LLM classification, not auto-refreshable)';
      } else if (meta.failure_mode_cache_status === 'missing') {
        suffix = ' — missing';
      } else {
        suffix = '';
      }
      lines.push(`Failure-mode taxonomy: ${fmtTs(meta.failure_mode_mtime) || '—'}${suffix}`);
    }
    lines.push('');

    lines.push('=== STATISTICAL / EVALUATION METRICS (DB-backed) ===');
    if (rm.compliance_score_median != null || rm.compliance_score_sd != null) {
      lines.push(`Corpus Compliance Score: mean ${rm.average_score != null ? rm.average_score.toFixed(1) : '—'}, median ${rm.compliance_score_median != null ? rm.compliance_score_median.toFixed(1) : '—'}, SD ${rm.compliance_score_sd != null ? rm.compliance_score_sd.toFixed(1) : '—'}, range ${rm.compliance_score_min != null ? rm.compliance_score_min.toFixed(0) : '—'}–${rm.compliance_score_max != null ? rm.compliance_score_max.toFixed(0) : '—'} (n=${rm.compliance_score_sample ?? 0} scored audits) — Paper 2 Table III "All" row`);
    }
    lines.push(`Score CV (median): ${rm.score_cv_median_pct != null ? rm.score_cv_median_pct.toFixed(1) + '%' : '—'} across ${rm.score_cv_pairs ?? 0} model-firewall pairs`);
    lines.push(`F5 Vendor Score Gap (Chk+Huawei vs other four): ${rm.parser_score_gap != null ? rm.parser_score_gap.toFixed(1) + ' pts' : '—'}` +
      (rm.parser_avg_vendor != null && rm.parser_avg_generic != null
        ? ` (other four ${rm.parser_avg_vendor.toFixed(1)}%, Chk+Huawei ${rm.parser_avg_generic.toFixed(1)}%, n=${rm.parser_vendor_count ?? 0}/${rm.parser_generic_count ?? 0}; not a parser artefact)` : ''));
    lines.push(`Audit Duration: median ${rm.audit_duration_median_s != null ? rm.audit_duration_median_s.toFixed(0) + 's' : '—'}` +
      `, mean ${rm.audit_duration_mean_s != null ? rm.audit_duration_mean_s.toFixed(0) + 's' : '—'}` +
      `, p99 ${rm.audit_duration_p99_s != null ? rm.audit_duration_p99_s.toFixed(0) + 's' : '—'}` +
      ` (n=${rm.audit_duration_sample ?? 0} audits with non-zero duration)`);
    lines.push(`Total LLM Wall-Clock: ${rm.total_llm_hours != null ? rm.total_llm_hours.toFixed(1) + 'h' : '—'}`);
    lines.push(`Non-LLM Latency: median ${fmtSec(rm.non_llm_seconds_median)}, p99 ${rm.non_llm_seconds_p99 != null ? rm.non_llm_seconds_p99.toFixed(1) + 's' : '—'} (n=${rm.non_llm_sample_size ?? 0})`);
    lines.push(`Dispatch Latency: median ${fmtSec(rm.dispatch_seconds_median)}, p99 ${fmtSec(rm.dispatch_seconds_p99)} (n=${rm.dispatch_sample_size ?? 0})`);
    lines.push(`Extraction Recovery (cascade tiers 2-4): ${rm.extraction_recovery_rate != null ? rm.extraction_recovery_rate.toFixed(1) + '%' : '—'} (clean=${rm.extraction_clean_count ?? 0}, recovered=${rm.extraction_recovered_count ?? 0}, total=${rm.extraction_total_observations ?? 0})`);
    lines.push('');

    // === PAPER 2 BREAKDOWNS (DB-derived) ===
    if (rm.alert_classification) {
      lines.push('=== ALERT CLASSIFICATION (severity tier per audit) ===');
      const ac = rm.alert_classification;
      ['critical','high','medium','low','compliant'].forEach(k => {
        const label = k.charAt(0).toUpperCase() + k.slice(1);
        const v = ac[k] || { n: 0, pct: 0 };
        lines.push(`${label.padEnd(10)}: ${String(v.n).padStart(4)}  (${v.pct.toFixed(1)}%)`);
      });
      lines.push('');
    }

    if (Array.isArray(rm.cascade_tier_histogram) && rm.cascade_tier_histogram.length) {
      lines.push('=== SCORE-EXTRACTION CASCADE TIER DISTRIBUTION ===');
      rm.cascade_tier_histogram.forEach(t => {
        lines.push(`${t.label.padEnd(24)}: ${String(t.count).padStart(4)}  (${t.share_pct.toFixed(1)}%)`);
      });
      lines.push('');
    }

    if (rm.rag_retrieval_health) {
      const rh = rm.rag_retrieval_health;
      const rag_ts = fmtTs((rm._meta || {}).rag_cache_mtime);
      lines.push(`=== RAG RETRIEVAL HEALTH (Paper 2 §V, hybrid H = ${rh.weighting_semantic ?? 0.7}·S_sem + ${rh.weighting_keyword ?? 0.3}·S_kw)${rag_ts ? ' [cache: ' + rag_ts + ']' : ''} ===`);
      lines.push(`Retrieval coverage: ${rh.coverage_pct != null ? rh.coverage_pct.toFixed(2) + '%' : '—'} (${rh.rag_hits ?? '—'} / ${rh.findings_total ?? '—'} findings above H≥${rh.confidence_cutoff ?? 0.30}; ${rh.below_cutoff ?? 0} below cutoff, left unmapped — no keyword fallback on the RAG path)`);
      lines.push(`Hybrid score distribution: median ${rh.hybrid_score_median ?? '—'}, p25 ${rh.hybrid_score_p25 ?? '—'}, p75 ${rh.hybrid_score_p75 ?? '—'}, min ${rh.hybrid_score_min ?? '—'}, max ${rh.hybrid_score_max ?? '—'}, IQR width ${rh.hybrid_score_iqr_width ?? '—'}`);
      lines.push('Score bands (relative to confidence cutoff 0.30):');
      lines.push(`  High   (H ≥ 0.515)         : ${rh.band_high_pct != null ? rh.band_high_pct.toFixed(1) + '%' : '—'}`);
      lines.push(`  Medium (0.418 ≤ H < 0.515) : ${rh.band_medium_pct != null ? rh.band_medium_pct.toFixed(1) + '%' : '—'}`);
      lines.push(`  Low    (H < 0.418)         : ${rh.band_low_pct != null ? rh.band_low_pct.toFixed(1) + '%' : '—'}`);
      lines.push(`  Below cutoff              : ${rh.band_below_cutoff_pct != null ? rh.band_below_cutoff_pct.toFixed(2) + '%' : '—'}`);
      lines.push('');
    }

    if (rm.inter_strategy_agreement) {
      const isa = rm.inter_strategy_agreement;
      const isa_ts = fmtTs((rm._meta || {}).inter_strategy_mtime);
      lines.push(`=== INTER-STRATEGY AGREEMENT (Paper 2 §V-A, n=${isa.n_unique_findings ?? 2967} unique findings)${isa_ts ? ' [cache: ' + isa_ts + ']' : ''} ===`);
      const rel = isa.reliability || {};
      const alpha = rel.krippendorff_alpha_family_nominal;
      const kappa = rel.fleiss_kappa_family;
      lines.push(`Krippendorff's α (family, nominal): ${alpha != null ? alpha.toFixed(3) : '—'}`);
      lines.push(`Fleiss' κ (family):                 ${kappa != null ? kappa.toFixed(3) : '—'}`);
      const cov = isa.coverage || {};
      lines.push(`Per-strategy coverage: S1=${cov.s1_keyword != null ? (cov.s1_keyword * 100).toFixed(1) + '%' : '—'}, S2=${cov.s2_rag != null ? (cov.s2_rag * 100).toFixed(1) + '%' : '—'}, S3=${cov.s3_llm_native != null ? (cov.s3_llm_native * 100).toFixed(1) + '%' : '—'}${isa.s3_coverage_n ? ` (S3 n=${isa.s3_coverage_n})` : ''}`);
      const pf = isa.pairwise_family_agreement || {};
      const pe = isa.pairwise_exact_agreement || {};
      const fmtPair = (k) => {
        const f = pf[k]; const e = pe[k];
        const label = k === 's1_s2' ? 'S1↔S2 (kw vs RAG)' : k === 's1_s3' ? 'S1↔S3 (kw vs LLM)' : 'S2↔S3 (RAG vs LLM)';
        const fam = f ? `family ${(f.agreement * 100).toFixed(1)}% (n=${f.n})` : '—';
        const ex = e ? `exact ${(e.agreement * 100).toFixed(1)}% (n=${e.n})` : '—';
        return `${label.padEnd(20)}: ${fam.padEnd(28)} | ${ex}`;
      };
      lines.push('Pairwise agreement (family / exact control):');
      lines.push(`  ${fmtPair('s1_s2')}`);
      lines.push(`  ${fmtPair('s1_s3')}`);
      lines.push(`  ${fmtPair('s2_s3')}`);
      const tw = isa.three_way_family_breakdown || {};
      const totN = isa.n_unique_findings || 2967;
      const pct = (n) => totN ? ((n / totN) * 100).toFixed(1) + '%' : '—';
      lines.push('Three-way family breakdown:');
      lines.push(`  All 3 agree      : ${String(tw.all_3_agree ?? '—').padStart(5)}  (${pct(tw.all_3_agree ?? 0)})`);
      lines.push(`  Partial (2 agree): ${String(tw.exactly_2_agree ?? '—').padStart(5)}  (${pct(tw.exactly_2_agree ?? 0)})`);
      lines.push(`  All 3 disagree   : ${String(tw.all_3_disagree ?? '—').padStart(5)}  (${pct(tw.all_3_disagree ?? 0)})`);
      lines.push(`  Shares above are of all ${totN} findings. Paper 2 V-A reports the same counts over the`);
      lines.push(`  ${isa.s3_coverage_n ?? 404}-finding three-way-eligible subset, where they read 26.7% and 20.0%.`);
      const H = isa.shannon_entropy_per_strategy_nats || {};
      lines.push(`Shannon entropy (family diversity, nats): S1=${H.s1_keyword ?? '—'}, S2=${H.s2_rag ?? '—'}, S3=${H.s3_llm_native ?? '—'} (S3 over its ${isa.s3_coverage_n ?? 404}-finding coverage subset)`);
      if (isa.s1_sc_concentration) {
        const sc = isa.s1_sc_concentration;
        lines.push(`S1 ${sc.family} concentration: ${sc.count}/${sc.total} = ${sc.share_pct.toFixed(1)}% (SC-07 fallback attractor)`);
      }
      lines.push('');
    }

    if (rm.consensus_diagnostics) {
      const cd = rm.consensus_diagnostics;
      const cd_ts = fmtTs((rm._meta || {}).consensus_diagnostics_mtime);
      lines.push(`=== CONSENSUS DIAGNOSTICS (Paper 2 §IV-B)${cd_ts ? ' [cache: ' + cd_ts + ']' : ''} ===`);
      lines.push(`ICC(2,1): ${cd.icc_value != null ? cd.icc_value.toFixed(3) : '—'} (${cd.icc_interpretation ?? '—'}) on n=${cd.n_pairs_used ?? '—'} consensus-eligible pairs`);
      lines.push(`Per-mechanism coverage:`);
      lines.push(`  A (majority-vote)      : ${cd.mechanism_a_coverage_rate != null ? (cd.mechanism_a_coverage_rate * 100).toFixed(1) + '%' : '—'}`);
      lines.push(`  B (tier-weighted mean) : ${cd.mechanism_b_coverage_rate != null ? (cd.mechanism_b_coverage_rate * 100).toFixed(1) + '%' : '—'}`);
      lines.push(`  C (variance threshold) : ${cd.mechanism_c_coverage_rate != null ? (cd.mechanism_c_coverage_rate * 100).toFixed(1) + '%' : '—'}`);
      lines.push('');
    }

    if (rm.failure_mode_taxonomy) {
      const fm = rm.failure_mode_taxonomy;
      const fm_ts = fmtTs((rm._meta || {}).failure_mode_mtime);
      const strict = fm.strict_subset || {};
      lines.push(`=== FAILURE-MODE TAXONOMY (Paper 2 §V-B, strict 3-way n=${strict.n_cases ?? 81})${fm_ts ? ' [cache: ' + fm_ts + ']' : ''} ===`);
      (strict.distribution || []).forEach(d => {
        lines.push(`${(d.category || '').padEnd(36)}: ${String(d.count).padStart(3)}  (${d.share_pct.toFixed(1)}%)`);
      });
      const conf = strict.confidence_distribution || {};
      if (conf.high || conf.medium) {
        lines.push(`Confidence: high ${conf.high?.pct?.toFixed(1) ?? '—'}%, medium ${conf.medium?.pct?.toFixed(1) ?? '—'}%, low ${conf.low?.pct?.toFixed(1) ?? '—'}%`);
      }
      const adv = strict.adversarial_review || {};
      if (adv.inter_classifier_agreement_pct != null) {
        lines.push(`Adversarial classifier agreement: ${adv.inter_classifier_agreement_pct.toFixed(1)}% (${adv.agree_count}/${adv.sample_size} blind stratified sub-sample)`);
      }
      const loose = fm.loose_100_case_set || {};
      if (loose.n_cases) {
        const gap = (loose.distribution || [])[0] || {};
        lines.push(`Looser 100-case set: ${gap.count}/${loose.n_cases} = ${gap.share_pct?.toFixed(1)}% ${gap.category} (precedence rule; S3-null dominates)`);
      }
      lines.push('');
    }


    if (Array.isArray(rm.per_provider_stats) && rm.per_provider_stats.length) {
      lines.push('=== PER-PROVIDER PERFORMANCE & LATENCY ===');
      lines.push('Provider         |   n | Mdl | Mean | Med  | SD   | IQR        | P50s | P99s');
      rm.per_provider_stats.forEach(p => {
        const iqr = (p.iqr_q1 != null && p.iqr_q3 != null) ? `${p.iqr_q1.toFixed(0)}-${p.iqr_q3.toFixed(0)}` : '-';
        lines.push(
          `${(p.provider || '').padEnd(16)} | ${String(p.audits).padStart(3)} | ${String(p.models).padStart(3)} | ${
            (p.mean_score != null ? p.mean_score.toFixed(1) : '-').padStart(4)} | ${
            (p.median_score != null ? p.median_score.toFixed(1) : '-').padStart(4)} | ${
            (p.sd_score != null ? p.sd_score.toFixed(1) : '-').padStart(4)} | ${iqr.padStart(10)} | ${
            (p.lat_p50_s != null ? String(p.lat_p50_s) : '-').padStart(4)} | ${
            (p.lat_p99_s != null ? String(p.lat_p99_s) : '-').padStart(4)}`);
      });
      lines.push('');
    }

    if (Array.isArray(rm.per_vendor_stats) && rm.per_vendor_stats.length) {
      lines.push('=== PER-VENDOR PERFORMANCE & LATENCY ===');
      lines.push('Vendor       |   n | Mean | Med  | SD   | IQR        | P50s | P99s');
      rm.per_vendor_stats.forEach(v => {
        const iqr = (v.iqr_q1 != null && v.iqr_q3 != null) ? `${v.iqr_q1.toFixed(0)}-${v.iqr_q3.toFixed(0)}` : '-';
        lines.push(
          `${(v.vendor || '').padEnd(12)} | ${String(v.audits).padStart(3)} | ${
            (v.mean_score != null ? v.mean_score.toFixed(1) : '-').padStart(4)} | ${
            (v.median_score != null ? v.median_score.toFixed(1) : '-').padStart(4)} | ${
            (v.sd_score != null ? v.sd_score.toFixed(1) : '-').padStart(4)} | ${iqr.padStart(10)} | ${
            (v.lat_p50_s != null ? String(v.lat_p50_s) : '-').padStart(4)} | ${
            (v.lat_p99_s != null ? String(v.lat_p99_s) : '-').padStart(4)}`);
      });
      lines.push('');
    }

    if (rm.parser_score_gap != null && rm.parser_avg_vendor != null && rm.parser_avg_generic != null) {
      lines.push('=== F5 VENDOR SCORE GAP (Paper 2 VI-A) ===');
      lines.push(`Cisco / Palo Alto / Juniper / Fortinet (n=${rm.parser_vendor_count}): ${rm.parser_avg_vendor.toFixed(1)}%`);
      lines.push(`Huawei USG + Check Point (n=${rm.parser_generic_count}):                  ${rm.parser_avg_generic.toFixed(1)}%`);
      lines.push(`Gap: ${rm.parser_score_gap.toFixed(1)} pp. NOT a parser artefact — all six vendors have dedicated parsers and the`);
      lines.push('generic fallback is never exercised. See the floor-censoring decomposition below.');
      lines.push('');
    }

    if (Array.isArray(rm.nist_family_distribution) && rm.nist_family_distribution.length) {
      const rag_ts = fmtTs((rm._meta || {}).rag_cache_mtime);
      lines.push(`=== NIST CONTROL FAMILY DISTRIBUTION (top-1 citations across mapped findings)${rag_ts ? ' [cache: ' + rag_ts + ']' : ''} ===`);
      rm.nist_family_distribution.slice(0, 10).forEach(f => {
        lines.push(`${(f.family || '').padEnd(4)}: ${String(f.citations).padStart(5)}  (${f.share_pct.toFixed(1)}%)`);
      });
      lines.push('');
    }


    if (rm.case_study_audit) {
      const cs = rm.case_study_audit;
      lines.push(`=== END-TO-END CASE STUDY (audit_history.id = ${cs.audit_id}) ===`);
      lines.push(`Device:    ${cs.device_name ?? '—'} (${cs.vendor ?? '—'})`);
      lines.push(`AI Model:  ${cs.ai_model ?? '—'}`);
      lines.push(`Score:     ${cs.score != null ? cs.score.toFixed(0) + '%' : '—'}`);
      lines.push(`Findings:  ${cs.findings ?? '—'}`);
      lines.push(`Duration:  ${cs.duration_s != null ? cs.duration_s + 's' : '—'}`);
      lines.push(`Audited:   ${cs.audit_date ?? '—'}`);
      lines.push('');
    }

    const dd = rm.drift_decomposition, fd = rm.floor_decomposition;
    const cst = rm.corpus_structure, sdt = rm.seeded_detection;
    if (dd && (dd.same_day || dd.across_days)) {
      lines.push('=== INTER-RUN DISPERSION: WITHIN- vs ACROSS-SESSION (Paper 2 IV-B) ===');
      lines.push(`Corpus span: ${dd.corpus_span_days ?? '-'} days | ${dd.restriction ?? ''}`);
      lines.push('Group        | pairs | mean SD | mean CV');
      [['same-day', dd.same_day], ['across-days', dd.across_days]].forEach(([lbl, g]) => {
        if (g) lines.push(`${lbl.padEnd(12)} | ${String(g.pairs).padStart(5)} | ${String(g.mean_sd).padStart(7)} | ${g.mean_cv_pct.toFixed(1).padStart(6)}%`);
      });
      lines.push('');
    }
    if (fd) {
      lines.push('=== F5 VENDOR GAP: FLOOR-CENSORING DECOMPOSITION (Paper 2 VI-A) ===');
      lines.push('Group      |   n | mean(all) | zero% | mean(score>0)');
      [['low_two', 'Chk+Huawei'], ['other_four', 'Other four']].forEach(([k, lbl]) => {
        const g = fd[k]; if (!g) return;
        lines.push(`${lbl.padEnd(10)} | ${String(g.n).padStart(3)} | ${String(g.mean_all).padStart(9)} | ${String(g.zero_pct).padStart(5)} | ${String(g.mean_nonzero).padStart(13)}`);
      });
      lines.push('Conditional on a non-zero score the groups are within 0.1 points: the gap is a zero-rate difference.');
      lines.push('');
    }
    if (sdt && sdt.overall) {
      lines.push('=== SEEDED-WEAKENING DETECTION (Paper 2 IV-A) ===');
      lines.push(`Overall: ${sdt.overall.recall_pct}% (${sdt.overall.detected}/${sdt.overall.opportunities}) across ${sdt.configurations_covered} configurations`);
      lines.push('Provider             | detected | opps | recall');
      Object.entries(sdt.per_provider || {}).forEach(([prov, v]) => {
        lines.push(`${prov.padEnd(20)} | ${String(v.detected).padStart(8)} | ${String(v.opportunities).padStart(4)} | ${v.recall_pct.toFixed(1).padStart(5)}%`);
      });
      lines.push('Bounds attention, not correctness. Providers with <10 opportunities are not comparable.');
      lines.push('');
    }
    if (cst && cst.configurations) {
      lines.push('=== CORPUS STRUCTURE (Paper 2 VII-D) ===');
      lines.push(`${cst.total_audits} audits over ${cst.configurations.length} configurations; top two carry ${cst.top_two_share_pct}%`);
      lines.push('Configuration                  | Vendor                      | Audits | Models');
      cst.configurations.forEach(c => lines.push(
        `${String(c.device || '').slice(0,30).padEnd(30)} | ${String(c.vendor || '').slice(0,27).padEnd(27)} | ${String(c.audits).padStart(6)} | ${String(c.models).padStart(6)}`));
      lines.push('');
    }

    lines.push('=== EVALUATION CONTEXT (Code-backed) ===');
    lines.push('CDM Coverage: 20/22 — all 6 vendor parsers (Cisco / Palo Alto / Juniper SRX / Fortinet / Huawei / Check Point)');
    lines.push('Severity Weights: -10 / -8 / -5 / -3 (Critical / High / Medium / Low)');
    lines.push('Hybrid RAG Score: 70/30 (semantic / keyword), cutoff 0.30, top-5 retrieval');
    lines.push('MAX_CONFIG_LENGTH: 50,000 chars (per-audit input cap)');
    lines.push('Wazuh Rule Levels: 15 / 12 / 8 / 3 / 1 (Critical / High / Medium / Low / Compliant)');
    lines.push('Test Configurations: 9 (1-2 per vendor, 30-80 rules)');
    lines.push('FW Vendors: 6 (Cisco FTD, Palo Alto, Juniper SRX, Fortinet, Huawei, Check Point)');
    lines.push('LLM Providers: 6 (Ollama local + 5 cloud APIs)');

    return lines.join('\n');
  };

  // Generic clipboard handler used by both Paper 1 and Paper 2 copy buttons.
  const copyToClipboard = async (text, successMessage) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts (HTTP / older browsers)
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopySnackbar({ open: true, message: successMessage });
    } catch (err) {
      console.error('Copy failed:', err);
      setCopySnackbar({ open: true, message: 'Copy failed — see console' });
    }
  };

  const handleCopyMetrics1 = () => copyToClipboard(buildMetrics1Text(), 'Design metrics copied to clipboard');
  const handleCopyMetrics2 = () => copyToClipboard(buildMetrics2Text(), 'Evaluation metrics copied to clipboard');

  // Fetch audit reports for recent audits and critical audits
  useEffect(() => {
    const fetchAuditReports = async () => {
      if (!auditHistory || auditHistory.length === 0) return;

      // Collect unique audits: last 5 + last 5 critical (score < 30)
      const recentAudits = auditHistory.slice(0, 5);
      const criticalAudits = auditHistory
        .filter(a => a.overall_score !== null && a.overall_score !== undefined && Number(a.overall_score) < 30)
        .slice(0, 5);
      const seenIds = new Set();
      const auditsToFetch = [];
      for (const audit of [...recentAudits, ...criticalAudits]) {
        if (!seenIds.has(audit.id)) {
          seenIds.add(audit.id);
          auditsToFetch.push(audit);
        }
      }

      const results = await Promise.all(
        auditsToFetch.map(audit =>
          fetch(`${process.env.REACT_APP_API_URL}/firewalls/${audit.firewall_id}/audit-reports?page=1&page_size=5`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
          })
            .then(res => res.ok ? res.json() : null)
            .then(data => {
              if (data) {
                const report = data.reports.find(r => r.audit_history_id === audit.id);
                return report ? { id: audit.id, report: [report] } : null;
              }
              return null;
            })
            .catch(() => null)
        )
      );
      const reports = {};
      results.forEach(r => { if (r) reports[r.id] = r.report; });
      setAuditReports(reports);
    };

    fetchAuditReports();
  }, [auditHistory]);


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const handleViewDetails = (audit) => {
    navigate(`/history`, { state: { selectedAudit: audit } });
  };

  const handleDownloadReport = async (reportId, filename) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/audit-reports/${reportId}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error downloading report:', error);
    }
  };

  // Get last 5 audit reports from audit history
  const lastFiveAudits = auditHistory?.slice(0, 5) || [];

  // Derive last audit timestamp from auditHistory
  const lastAuditDate = auditHistory && auditHistory.length > 0 ? auditHistory[0].audit_date : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Dashboard
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {lastAuditDate && (
            <Typography variant="caption" color="text.secondary">
              Last audit: {formatLocalDateTime(lastAuditDate)}
            </Typography>
          )}
          <Tooltip title={loading ? 'Refreshing…' : 'Refresh data'}>
            <span>
              <IconButton
                size="small"
                onClick={() => refresh && refresh()}
                disabled={loading || !refresh}
                aria-label="Refresh dashboard data"
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                  '@keyframes spin': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                  },
                  '& svg': loading ? { animation: 'spin 1s linear infinite' } : {},
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Row 1: Infrastructure stats — compact single row */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Tooltip arrow title={METRIC_TOOLTIPS['infra.devices']}>
            <Paper onClick={() => navigate('/firewalls')} sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{stats?.totalFirewalls || 0}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Devices</Typography>
            </Paper>
          </Tooltip>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Tooltip arrow title={METRIC_TOOLTIPS['infra.fw_vendors']}>
            <Paper onClick={() => navigate('/firewalls')} sx={{ background: 'linear-gradient(135deg, #388e3c 0%, #66bb6a 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{stats?.totalTypes || 0}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Device Vendors</Typography>
            </Paper>
          </Tooltip>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Tooltip arrow title={METRIC_TOOLTIPS['infra.ai_vendors']}>
            <Paper onClick={() => navigate('/llms')} sx={{ background: 'linear-gradient(135deg, #0288d1 0%, #4fc3f7 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{stats?.aiVendors || 0}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>AI Vendors</Typography>
            </Paper>
          </Tooltip>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Tooltip arrow title="Locally-hosted LLM models (Ollama-served). Zero API cost, on-premise inference. Click to open the LLM catalogue.">
            <Paper onClick={() => navigate('/llms')} sx={{ background: 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{stats?.offlineModels || 0}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Local LLMs</Typography>
            </Paper>
          </Tooltip>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Tooltip arrow title="Cloud LLM models on paid tiers (OpenAI, Anthropic Claude, Google AI Studio). Consume the paid tier weighting (1.0) in Consensus Mechanism B.">
            <Paper onClick={() => navigate('/llms')} sx={{ background: 'linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{stats?.onlineModels || 0}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Paid LLMs</Typography>
            </Paper>
          </Tooltip>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Tooltip arrow title="Cloud LLM models on free tiers (Groq, OpenRouter). Consume the free tier weighting (0.7) in Consensus Mechanism B.">
            <Paper onClick={() => navigate('/llms')} sx={{ background: 'linear-gradient(135deg, #00897b 0%, #4db6ac 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{stats?.freeModels || 0}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Free LLMs</Typography>
            </Paper>
          </Tooltip>
        </Grid>
      </Grid>

      {/* Row 2: Audit overview + Alert severity — 7 cards in one row */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md>
          <Tooltip arrow title={METRIC_TOOLTIPS['infra.total']}>
            <Paper onClick={() => navigate('/history')} sx={{ background: 'linear-gradient(135deg, #0288d1 0%, #4fc3f7 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{stats?.directAudits || 0}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Audits</Typography>
            </Paper>
          </Tooltip>
        </Grid>
        <Grid item xs={6} sm={4} md>
          <Tooltip arrow title={METRIC_TOOLTIPS['infra.avg_score']}>
            <Paper onClick={() => navigate('/history')} sx={{ background: 'linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{Math.round(stats?.averageScore || 0)}%</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Avg Score</Typography>
            </Paper>
          </Tooltip>
        </Grid>
        <Grid item xs={6} sm={4} md>
          <Tooltip arrow title={METRIC_TOOLTIPS['alert.critical']}>
            <Paper onClick={() => navigate('/history?socAlert=Critical+Alert')} sx={{ background: 'linear-gradient(135deg, #d32f2f 0%, #e57373 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{stats?.criticalAlerts || 0}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Critical</Typography>
            </Paper>
          </Tooltip>
        </Grid>
        <Grid item xs={6} sm={4} md>
          <Tooltip arrow title={METRIC_TOOLTIPS['alert.high']}>
            <Paper onClick={() => navigate('/history?socAlert=High+Alert')} sx={{ background: 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{stats?.highAlerts || 0}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>High</Typography>
            </Paper>
          </Tooltip>
        </Grid>
        <Grid item xs={6} sm={4} md>
          <Tooltip arrow title={METRIC_TOOLTIPS['alert.medium']}>
            <Paper onClick={() => navigate('/history?socAlert=Medium+Alert')} sx={{ background: 'linear-gradient(135deg, #fbc02d 0%, #fff176 100%)', color: 'rgba(0,0,0,0.87)', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{stats?.mediumAlerts || 0}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Medium</Typography>
            </Paper>
          </Tooltip>
        </Grid>
        <Grid item xs={6} sm={4} md>
          <Tooltip arrow title={METRIC_TOOLTIPS['alert.low']}>
            <Paper onClick={() => navigate('/history?socAlert=Low+Alert')} sx={{ background: 'linear-gradient(135deg, #388e3c 0%, #66bb6a 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{stats?.lowAlerts || 0}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Low</Typography>
            </Paper>
          </Tooltip>
        </Grid>
        <Grid item xs={6} sm={4} md>
          <Tooltip arrow title={METRIC_TOOLTIPS['alert.compliant']}>
            <Paper onClick={() => navigate('/history?socAlert=Compliant')} sx={{ background: 'linear-gradient(135deg, #0d47a1 0%, #42a5f5 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{stats?.compliantAlerts || 0}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Compliant</Typography>
            </Paper>
          </Tooltip>
        </Grid>
      </Grid>

      {/* Research Metrics 1 + 2 toggle row — two paper-specific sections.
          Admin-only: these panels publish the ground-truth precision figures
          (27%/39% strict/lenient and the c/p/w three-way table). Annotator
          accounts must not see them — knowing the expected verdict
          distribution before annotating biases the inter-annotator study. */}
      {isAdmin && (
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: (showMetrics1 || showMetrics2) ? 1.5 : 2 }}>
        <Button
          variant={showMetrics1 ? 'contained' : 'outlined'}
          size="small"
          color="primary"
          startIcon={<ScienceIcon />}
          endIcon={<ExpandMoreIcon sx={{ transform: showMetrics1 ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />}
          onClick={toggleMetrics1}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Design Metrics
        </Button>
        <Button
          variant={showMetrics2 ? 'contained' : 'outlined'}
          size="small"
          color="secondary"
          startIcon={<ScienceIcon />}
          endIcon={<ExpandMoreIcon sx={{ transform: showMetrics2 ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />}
          onClick={toggleMetrics2}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Evaluation Metrics
        </Button>
        {(showMetrics1 || showMetrics2) && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Source: NSCAT database (live) and source code constants
          </Typography>
        )}
      </Box>
      )}

      <Collapse in={isAdmin && showMetrics1} timeout="auto" unmountOnExit>
        {/* Paper 1 (Design) — headline DB-backed validation + architectural constants */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="overline" color="primary" sx={{ display: 'block', letterSpacing: 0.6, fontWeight: 700 }}>
            Design Metrics — Paper 1: DB-backed headline validation
          </Typography>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={handleCopyMetrics1}
            disabled={loading}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Copy Design Metrics
          </Button>
          <Tooltip arrow title="Open the audit report reproduced as Figure 8 of Paper 1 (Palo Alto PAN-OS). Opens in a new tab; download from the browser's PDF viewer.">
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              startIcon={<PictureAsPdfIcon fontSize="small" />}
              onClick={openSampleReport}
              sx={{ textTransform: 'none', fontSize: '0.75rem', ml: 1 }}
            >
              Sample Report (Fig. 8)
            </Button>
          </Tooltip>
        </Box>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4} md>
            <Paper onClick={() => navigate('/history')} sx={{ background: 'linear-gradient(135deg, #455a64 0%, #78909c 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{(rm.total_findings ?? 0).toLocaleString()}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Total Findings</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>All compliance observations across audits</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Paper onClick={() => navigate('/history?reportStatus=success')} sx={{ background: 'linear-gradient(135deg, #2e7d32 0%, #81c784 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{(rm.pdf_success_rate ?? 0).toFixed(2)}%</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>PDF Success ({rm.pdf_generated ?? 0}/{rm.pdf_total ?? 0})</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Audits with stored PDF report</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Paper onClick={() => navigate('/syslog')} sx={{ background: 'linear-gradient(135deg, #1565c0 0%, #64b5f6 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{(rm.syslog_success_rate ?? 0).toFixed(0)}%</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Syslog ({rm.syslog_success ?? 0}/{rm.syslog_total ?? 0})</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>SIEM dispatches successfully delivered</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Paper onClick={() => navigate('/soar')} sx={{ background: 'linear-gradient(135deg, #6a1b9a 0%, #ba68c8 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{(rm.soar_success_rate ?? 0).toFixed(1)}%</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>SOAR ({rm.soar_success ?? 0}/{rm.soar_total ?? 0})</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Webhooks delivered to playbooks</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Paper onClick={() => navigate('/llms')} sx={{ background: 'linear-gradient(135deg, #00695c 0%, #4db6ac 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 }, transition: 'transform 0.15s, box-shadow 0.15s' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{rm.distinct_models ?? 0}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>LLM Models Used</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Distinct AI models in audit history</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Paper sx={{ background: 'linear-gradient(135deg, #c62828 0%, #ef9a9a 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{rm.extraction_failures ?? 0}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Extraction Failures</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Audits with no recoverable score</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip title={`MAX(LENGTH(firewall_credentials.configuration)) over all stored configs. Cap is MAX_CONFIG_LENGTH = 50,000 chars.`} arrow>
              <Paper sx={{ background: 'linear-gradient(135deg, #6d4c41 0%, #bcaaa4 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{rm.max_config_chars != null ? `${(rm.max_config_chars / 1000).toFixed(1)}k` : '—'}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Max Config Size{rm.max_config_chars != null ? ` (${rm.max_config_chars.toLocaleString()} chars)` : ''}
                </Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Largest configuration in the dataset</Typography>
              </Paper>
            </Tooltip>
          </Grid>
        </Grid>

        {/* Code-backed: static constants verifiable from source code */}
        <Typography variant="overline" color="primary" sx={{ display: 'block', mb: 0.5, letterSpacing: 0.6, fontWeight: 700 }}>
          Architectural Constants (Code-backed)
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md>
            <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>1,014</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>NIST SP 800-53 Controls</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Pre-embedded in ChromaDB</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="22 entity-level fields (vendor model/version, ACL default action, logging targets, admin access controls, interfaces, routing table, system NTP/DNS/syslog, antivirus/IDS/VPN flags, compliance metadata) + 10 per-rule fields (rule_id, src/dst IP/port, protocol, service, action, enabled, description) + 2 enums (AccessProtocol: SSH/HTTPS/HTTP/TELNET/LOCAL; DefaultAction: PERMIT/DENY).">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>34</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>CDM Fields (22+10+2)</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Entity + rule + enum normalisation schema</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="All 6 vendor-specific parsers (Cisco FTD, Palo Alto, Juniper SRX, Fortinet, Huawei USG, Check Point) populate 20 of 22 CDM entity fields. The two stub fields (interfaces, routing_table) return empty lists across all vendors pending future per-vendor extraction logic. A defensive fallback parser is retained for unrecognised firewall_type values but is not invoked in production.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>20/22</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>CDM Coverage (all 6 vendors)</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Cisco / PAN / SRX / Forti / Huawei / CP</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>1024-d</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Embedding (mxbai-embed-large)</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Vector dimensionality for RAG retrieval</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>70/30</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Hybrid RAG (sem/kw)</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Semantic + keyword score weighting</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>−10/−8/−5/−3</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Severity Weights</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Critical / High / Medium / Low deductions</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>50,000</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>MAX_CONFIG_LENGTH (chars)</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Per-audit configuration size cap</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="asyncio.Semaphore(5) caps concurrent compliance checks across the entire audit pipeline, preventing LLM API rate-limit saturation and CPU/memory contention from parallel processing. A separate semaphore caps Ollama-specific concurrency.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>5</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>MAX_CONCURRENT_CHECKS</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Async semaphore concurrency limit</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>6 / 6</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>FW Vendors / LLM Providers</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Supported firewall and AI integrations</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title={(() => {
              const pm = locStats.provider_models || {};
              return `Per-provider LLM model breakdown (matches Paper 1 Table V): Ollama ${pm.ollama ?? '—'} local models, Groq ${pm.groq ?? '—'}, OpenAI ${pm.openai ?? '—'}, OpenRouter ${pm.openrouter ?? '—'}, Google AI Studio ${pm.google_ai_studio ?? '—'}, Anthropic Claude ${pm.anthropic_claude ?? '—'}.`;
            })()}>
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                  {(() => {
                    const pm = locStats.provider_models;
                    if (!pm) return '—';
                    const total = Object.values(pm).reduce((a, b) => a + (Number(b) || 0), 0);
                    return total;
                  })()}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Per-Provider Models</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>
                  {(() => {
                    const pm = locStats.provider_models;
                    if (!pm) return 'Ollama / Groq / OpenAI / OR / Google / Anthropic';
                    return `Ol ${pm.ollama ?? '—'} · Gr ${pm.groq ?? '—'} · OA ${pm.openai ?? '—'} · OR ${pm.openrouter ?? '—'} · Go ${pm.google_ai_studio ?? '—'} · An ${pm.anthropic_claude ?? '—'}`;
                  })()}
                </Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{fmtN(locStats.backend_loc)}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Backend LOC</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Python backend source lines</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{fmtN(locStats.frontend_loc)}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Frontend LOC</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>React 18 frontend source lines</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>30</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Utility Modules</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Python service / parser modules</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>12</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>PostgreSQL Models</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>SQLAlchemy ORM table classes</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="React 18 route-level pages: Dashboard, Firewalls, Audit History (Reports), SIEM, SOAR, LLMs, AI Chat, NIST Browser, User Management, Database Backup, Login, plus four research-paper sub-pages (MVP Overview, Market Validation, Pitch, Provisional Patent).">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>15</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Page Components</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>React route-level pages</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="Empirical token estimator: ~4 characters per token (0.25 tokens/char). Used by ConfigChunker to decide whether configuration text exceeds the model's context budget before submission.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>0.25</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Tokens / Char</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Chunk-trigger token estimator</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="ConfigChunker activates when estimated tokens (chars × 0.25) exceed 50% of the model's context window. Reserves the remaining 50% for the prompt template (header, vendor hints, NIST guide, JSON schema) plus the response.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>50%</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Chunk Trigger</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Of model context window</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="ChromaDB candidates with hybrid score below 0.30 are dropped before top-5 re-ranking. Hybrid score = 0.7 × semantic similarity + 0.3 × keyword overlap. Suppresses weak NIST mappings that would degrade traceability quality.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>0.30</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>RAG Cutoff</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Min hybrid score for retention</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="Top 5 NIST SP 800-53 controls retrieved per finding from ChromaDB. Used in (1) PDF traceability cards showing per-finding retrieval scores, (2) chatbot NIST-lookup mode with 10-message conversation context, and (3) prompt assembly NIST guide injection.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>top-{fmt(locStats.rag_top_k)}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>RAG Retrieval</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Chatbot / finding-mapping</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="Path A — audit-time prompt enrichment. Before each compliance check, the top 3 NIST SP 800-53 controls are retrieved from ChromaDB by semantic similarity and injected into the LLM prompt as a 'NIST guide' block. Distinct from the top-5 path used for the finding→control mapping and the chatbot.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>top-{fmt(locStats.rag_audit_time_top_k)}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Audit-Time RAG (Path A)</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Prompt enrichment per check</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="Compliance checks dispatched in parallel batches of 10 via asyncio.gather, balancing throughput with provider rate limits. Each batch awaits completion before the next is dispatched.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>10</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Batch Size</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Concurrent check batch</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="asyncio.wait_for ceiling on the entire audit pipeline (1,800 s = 30 min). If batches don't all complete within this window, the audit is killed and marked failed; the frontend's polling loop also stops at the same 30-minute mark.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>1,800s</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Global Timeout</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Per-audit hard limit (30 min)</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="HS256-signed access tokens expire 8 hours after issue, requiring re-authentication. Balances daily-use UX (no constant re-login) against the exposure window for stolen credentials.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>8h</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>JWT Expiry</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>HS256 access-token lifetime</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="ConfigSectionParser splits each configuration into 10–17 vendor-specific sections of ~3,000 chars. Per-vendor: Fortinet 17, Cisco 16, Check Point 15, Huawei 14, Palo Alto 11, Juniper 10. Each section is audited independently for sharper LLM focus at the cost of cross-section context.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>10–17</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Sections / Config</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>~3,000 chars each (chunked)</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>15/12/8/3/1</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>Wazuh Levels</Typography>
              <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Critical/High/Med/Low/Compliant</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="SSH connection/banner/auth timeouts default to 10 s; Cisco FTD raised to 30 s due to slower handshake. Per-command read timeouts vary by paging behaviour: 30 s Juniper, 120 s Huawei, 180 s Fortinet (which also requires a 50-attempt pagination loop).">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>30/120/180s</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>SSH Read Timeouts</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Juniper / Huawei / Fortinet</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="ScoreExtractor cascade with 4 levels: L1 reads structured compliance_score field (GPT-4o, Claude resolve here); L2 parses JSON from raw text (code blocks, then bare braces); L3 applies 5 regex patterns for prose-embedded scores; L4 deducts by severity from 100. Scores clamped to [0.0, 100.0].">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>4 / 5</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Cascade Tiers / Regex</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Score-extraction levels and L3 patterns</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="PDF traceability cards classify each finding's NIST-mapping confidence by hybrid retrieval score: High ≥ 0.515, Medium ≥ 0.418, Low < 0.418. Thresholds recalibrated from the original 0.70/0.50 defaults to the empirical p75/p25 of the observed hybrid-score distribution (median 0.47).">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>0.515 / 0.418</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>RAG Confidence Tiers</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>High ≥ 0.515, Medium ≥ 0.418, Low &lt; 0.418</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title={(() => {
              const rs = locStats.rag_retrieval_stats;
              if (!rs) return 'Median hybrid score across every finding in the audit set, with p25/p75. Computed offline by scripts/compute_rag_metrics.py walking all COMPLETED audits. Retrieval-confidence signal — NOT mapping correctness (see §VIII-B L5).';
              return `Median hybrid score = ${rs.hybrid_score_median?.toFixed?.(3) ?? '—'} (p25 ${rs.hybrid_score_p25?.toFixed?.(3) ?? '—'}, p75 ${rs.hybrid_score_p75?.toFixed?.(3) ?? '—'}) across ${rs.rag_hits ?? 0} RAG hits from ${rs.findings_total ?? 0} findings. Retrieval confidence, not accuracy (see §VIII-B L5).`;
            })()}>
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                  {locStats.rag_retrieval_stats?.hybrid_score_median != null
                    ? locStats.rag_retrieval_stats.hybrid_score_median.toFixed(2)
                    : '—'}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Hybrid Score (median)</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>
                  {locStats.rag_retrieval_stats
                    ? `p25 ${locStats.rag_retrieval_stats.hybrid_score_p25?.toFixed?.(2) ?? '—'} · p75 ${locStats.rag_retrieval_stats.hybrid_score_p75?.toFixed?.(2) ?? '—'}`
                    : 'Run compute_rag_metrics.py to populate'}
                </Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title={(() => {
              const rs = locStats.rag_retrieval_stats;
              if (!rs) return 'Distribution of findings by RAG confidence tier (High ≥ 0.515, Medium ≥ 0.418, Low < 0.418, recalibrated to empirical p75/p25). Computed offline by scripts/compute_rag_metrics.py.';
              return `Across ${rs.rag_hits ?? 0} RAG hits: ${rs.pct_high?.toFixed?.(1)}% High, ${rs.pct_medium?.toFixed?.(1)}% Medium, ${rs.pct_low?.toFixed?.(1)}% Low. Below-cutoff (<0.30): ${rs.pct_below_cutoff?.toFixed?.(1) ?? '—'}%.`;
            })()}>
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                  {locStats.rag_retrieval_stats?.pct_high != null
                    ? `${locStats.rag_retrieval_stats.pct_high.toFixed(0)}%`
                    : '—'}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>High Confidence Mix</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>
                  {locStats.rag_retrieval_stats
                    ? `Med ${locStats.rag_retrieval_stats.pct_medium?.toFixed?.(0) ?? '—'}% · Low ${locStats.rag_retrieval_stats.pct_low?.toFixed?.(0) ?? '—'}%`
                    : 'High/Med/Low share of hybrid scores'}
                </Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title={(() => {
              const rs = locStats.rag_retrieval_stats;
              if (!rs) return 'Total findings analysed and RAG hits across all COMPLETED audits in the dataset. Computed offline by scripts/compute_rag_metrics.py.';
              return `${rs.findings_total ?? 0} findings extracted from ${rs.audits_with_findings ?? 0} audits (${rs.audits_walked ?? 0} walked); ${rs.rag_hits ?? 0} produced a NIST hit. Cache generated ${new Date((rs.generated_at ?? 0) * 1000).toLocaleString()}.`;
            })()}>
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                  {locStats.rag_retrieval_stats?.findings_total != null
                    ? locStats.rag_retrieval_stats.findings_total.toLocaleString()
                    : '—'}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Findings Analysed</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>
                  {locStats.rag_retrieval_stats
                    ? `${locStats.rag_retrieval_stats.rag_hits?.toLocaleString?.() ?? '—'} RAG hits`
                    : 'Run compute_rag_metrics.py'}
                </Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="If the Shuffle SOAR webhook fails, NSCAT retries up to 3 times with exponential backoff: 2¹ s, 2² s, 2³ s = 2 s, 4 s, 8 s between attempts. After 3 failures the audit is marked SOAR-undelivered but other dispatch paths (syslog, PDF) continue independently.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>3 × 2ⁿ s</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>SOAR Retry / Backoff</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Up to 3 retries, exponential backoff</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="End-to-end dispatch latency from audit completion to SIEM ingestion is under 2 seconds. Covers syslog/Wazuh delivery and the SOAR webhook fire-and-forget path (excluding the optional retry window on failure).">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>&lt;{fmt(locStats.dispatch_latency_max_seconds)}s</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Dispatch Latency</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Audit completion to SIEM ingestion</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="Shuffle routes the SOAR payload into one of 4 severity-tagged email templates (Critical / High / Medium / Low). Compliant audits are not emailed. Templates share layout; the subject line and remediation deadline change per tier.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>4</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Email Templates</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>One per alerting severity tier</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="Per-tier remediation SLA delivered with the SOAR email: Critical 24 hours, High 48 hours, Medium 72 hours, Low 1 week. Compliant audits are not emailed. Network team must remediate non-compliant findings and confirm back within the specified window.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>24h / 1w</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Critical / Low SLA</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>24h C · 48h H · 72h M · 1w L · Compliant: not emailed</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="Each prompt comprises 9 parts: header, check ID, NIST control mapping, device context, configuration text (≤3,000 chars), vendor-specific remediation hints, task description, embedded NIST guide, and a mandatory JSON schema. All 36 model configurations receive identical prompt structure — no model-specific tuning.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>9</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Prompt Parts</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Header + check + NIST + context + …</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="Mandatory JSON response schema with 7 fields: status, nist_sp_800_53, summary, issue, fix, verify, risk_level. Enforced for every model output; missing fields trigger fallback to the next extraction-cascade tier.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>7</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>JSON Response Fields</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>status / NIST / summary / issue / fix / verify / risk</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title="Nine seeded firewall configurations across six vendors form the evaluation corpus: 1–2 per vendor with 30–80 rules each. Configurations are deliberately seeded with policy violations (deny-all gaps, missing logging, overly permissive rules, zone-traversal flaws) to stress the audit pipeline.">
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>9</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Test Configs</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>1–2 per vendor, 30–80 rules</Typography>
              </Paper>
            </Tooltip>
          </Grid>
        </Grid>
      </Collapse>

      <Collapse in={isAdmin && showMetrics2} timeout="auto" unmountOnExit>
        {/* Paper 2 (Evaluation) — statistical/distributional metrics */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="overline" color="secondary" sx={{ display: 'block', letterSpacing: 0.6, fontWeight: 700 }}>
            Evaluation Metrics — Paper 2: statistical / distributional analysis
          </Typography>
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={handleCopyMetrics2}
            disabled={loading}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Copy Evaluation Metrics
          </Button>
        </Box>
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md>
            <Tooltip arrow title={METRIC_TOOLTIPS['metrics.score_cv']}>
              <Paper sx={{ background: 'linear-gradient(135deg, #4527a0 0%, #9575cd 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'help' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{rm.score_cv_median_pct != null ? `${rm.score_cv_median_pct.toFixed(1)}%` : '—'}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Score CV (median, {rm.score_cv_pairs ?? 0} pairs)</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Variability across model–firewall reruns</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip title={METRIC_TOOLTIPS['metrics.audit_duration']} arrow>
              <Paper sx={{ background: 'linear-gradient(135deg, #283593 0%, #7986cb 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{rm.audit_duration_median_s != null ? `${rm.audit_duration_median_s.toFixed(0)}s` : '—'}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Audit Duration (median{rm.audit_duration_mean_s != null ? `, mean ${rm.audit_duration_mean_s.toFixed(0)}s` : ''})
                </Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>End-to-end audit time per run</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip title={METRIC_TOOLTIPS['metrics.llm_wallclock']} arrow>
              <Paper sx={{ background: 'linear-gradient(135deg, #5d4037 0%, #a1887f 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{rm.total_llm_hours != null ? `${rm.total_llm_hours.toFixed(1)}h` : '—'}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Total LLM Hours</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Wall-clock time across all audits</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip title={METRIC_TOOLTIPS['metrics.non_llm_lat']} arrow>
              <Paper sx={{ background: 'linear-gradient(135deg, #00695c 0%, #4db6ac 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                  {rm.non_llm_seconds_median != null ? `${rm.non_llm_seconds_median.toFixed(2)}s` : '—'}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Non-LLM Latency (median{rm.non_llm_seconds_p99 != null ? `, p99 ${rm.non_llm_seconds_p99.toFixed(1)}s` : ''})
                </Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Pipeline time excluding LLM API calls</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip title={METRIC_TOOLTIPS['metrics.dispatch_lat']} arrow>
              <Paper sx={{ background: 'linear-gradient(135deg, #1565c0 0%, #4fc3f7 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                  {rm.dispatch_seconds_median != null ? `${rm.dispatch_seconds_median.toFixed(2)}s` : '—'}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Dispatch Latency (median{rm.dispatch_seconds_p99 != null ? `, p99 ${rm.dispatch_seconds_p99.toFixed(2)}s` : ''})
                </Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Audit completion to last dispatch path</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip title={METRIC_TOOLTIPS['metrics.extraction_rec']} arrow>
              <Paper sx={{ background: 'linear-gradient(135deg, #ad1457 0%, #f06292 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                  {rm.extraction_recovery_rate != null ? `${rm.extraction_recovery_rate.toFixed(1)}%` : '—'}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Extraction Recovery (cascade tiers 2-4)
                </Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Sections rescued by fallback strategies</Typography>
              </Paper>
            </Tooltip>
          </Grid>
        </Grid>

        {/* Evaluation Context — design constants that affect interpretation of Paper 2 results */}
        <Typography variant="overline" color="secondary" sx={{ display: 'block', mb: 0.5, letterSpacing: 0.6, fontWeight: 700 }}>
          Evaluation Context (Code-backed)
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md>
            <Tooltip title="All 6 vendor-specific parsers (Cisco FTD, Palo Alto, Juniper SRX, Fortinet, Huawei USG, Check Point) populate 20 of 22 CDM entity fields. The two stub fields (interfaces, routing_table) return empty lists across all vendors. Drives interpretation of per-vendor scores in Paper 2." arrow>
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>20/22</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>CDM Coverage (all 6 vendors)</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Cisco / PAN / SRX / Forti / Huawei / CP</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip title="Per-finding score deductions used by the L4 cascade tier. Drives compliance score distribution." arrow>
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>−10/−8/−5/−3</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Severity Weights</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Critical / High / Medium / Low deductions</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip title="Hybrid RAG score = 0.7×semantic + 0.3×keyword. Affects RAG retrieval quality measured in Paper 2 RQ4." arrow>
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>70/30</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Hybrid RAG (sem/kw)</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Semantic + keyword score weighting</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip title="Per-audit configuration size cap. Truncates evaluation inputs above this size." arrow>
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>50,000</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>MAX_CONFIG_LENGTH (chars)</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Per-audit configuration size cap</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip title="Wazuh rule.level mapping per severity tier. Defines alert tier evaluation buckets." arrow>
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>15/12/8/3/1</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Wazuh Levels</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Critical/High/Med/Low/Compliant</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip title="9 firewall configurations across 6 vendors form the evaluation corpus. Defines Paper 2 sample N." arrow>
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>9</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>Test Configs</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>1–2 per vendor, 30–80 rules</Typography>
              </Paper>
            </Tooltip>
          </Grid>
          <Grid item xs={6} sm={4} md>
            <Tooltip title="6 firewall vendors integrated. Defines per-vendor analysis dimensions." arrow>
              <Paper sx={{ background: 'linear-gradient(135deg, #37474f 0%, #607d8b 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>6 / 6</Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>FW Vendors / LLM Providers</Typography>
                <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>Supported firewall and AI integrations</Typography>
              </Paper>
            </Tooltip>
          </Grid>
        </Grid>

        {/* === PAPER 2 BREAKDOWNS (DB-derived) === */}

        {/* Data-source freshness banner — content-aware status (current/outdated/missing) */}
        {rm._meta && (() => {
          const m = rm._meta;
          const tw = cacheChip(m.three_way_cache_status, m.three_way_mapping_mtime, {
            coverage_label: m.three_way_gold_n != null ? `n=${m.three_way_gold_n}` : null,
            refresh_cmd: 'scripts/compute_three_way_mapping.py',
          });
          const rag = cacheChip(m.rag_cache_status, m.rag_cache_mtime, {
            coverage_label: m.rag_cache_audits_walked != null
              ? `${m.rag_cache_audits_walked} audits, ${m.rag_cache_findings_total ?? '?'} findings`
              : null,
            audits_added_since: m.audits_added_since_rag_cache,
            refresh_cmd: 'scripts/compute_rag_metrics.py',
          });
          const worst = [m.three_way_cache_status, m.rag_cache_status].includes('outdated') ? 'warning'
                      : [m.three_way_cache_status, m.rag_cache_status].includes('missing') ? 'error'
                      : 'info';
          return (
            <Alert severity={worst} variant="outlined" sx={{ mb: 2, py: 0.5 }} icon={false}>
              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                <strong>Data-source freshness:</strong>{' '}
                <Chip size="small" label={`DB live · ${fmtTs(m.fetched_at) || 'now'}`} color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 18, mr: 0.5 }} />
                <Tooltip title="Three-way NIST mapping accuracy — cached output of scripts/compute_three_way_mapping.py. Status compares the cache's gold-standard sample size against the source ragGroundTruth.json." arrow>
                  <Chip size="small" label={`Three-way: ${tw.text}`} color={tw.color} variant="outlined" sx={{ fontSize: '0.65rem', height: 18, mr: 0.5 }} />
                </Tooltip>
                <Tooltip title={`RAG retrieval coverage + NIST family distribution — cached output of scripts/compute_rag_metrics.py. Status compares cache audit count against current DB and checks for newer audits since cache was generated.${m.audits_added_since_rag_cache ? ' Currently ' + m.audits_added_since_rag_cache + ' new audits since the cache was last regenerated.' : ''}`} arrow>
                  <Chip size="small" label={`RAG: ${rag.text}`} color={rag.color} variant="outlined" sx={{ fontSize: '0.65rem', height: 18, mr: 0.5 }} />
                </Tooltip>
                {fmtTs(m.ground_truth_mtime) && (
                  <Tooltip title="Hand-annotated 100-finding gold standard from the first author (ragGroundTruth.json). Not regenerated; updated only when re-annotated." arrow>
                    <Chip size="small" label={`Gold-standard · ${fmtTs(m.ground_truth_mtime)}`} color="default" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                  </Tooltip>
                )}
              </Typography>
            </Alert>
          );
        })()}

        {/* Corpus Compliance Score Distribution (Paper 2 Table III "All" row) */}
        {rm.compliance_score_median != null && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="overline" color="secondary" sx={{ letterSpacing: 0.6, fontWeight: 700 }}>
                Corpus Compliance Score Distribution (n = {rm.compliance_score_sample ?? 0} scored audits)
              </Typography>
              <Chip size="small" label="Paper 2 Table III" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
              <Chip size="small" label="DB live" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Compliance-score summary across all scored audits. The corpus mean (39%) with no fully-compliant verdicts is a headline Paper 2 finding.
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {[
                { key: 'mean', tipKey: 'compliance.mean', label: 'Mean', val: rm.average_score, fmt: v => v != null ? v.toFixed(1) : '—', bg: 'linear-gradient(135deg, #1b5e20 0%, #66bb6a 100%)' },
                { key: 'median', tipKey: 'compliance.median', label: 'Median', val: rm.compliance_score_median, fmt: v => v != null ? v.toFixed(1) : '—', bg: 'linear-gradient(135deg, #0d47a1 0%, #64b5f6 100%)' },
                { key: 'sd', tipKey: 'compliance.sd', label: 'SD', val: rm.compliance_score_sd, fmt: v => v != null ? v.toFixed(1) : '—', bg: 'linear-gradient(135deg, #6a1b9a 0%, #ba68c8 100%)' },
                { key: 'range', tipKey: 'compliance.range', label: 'Range', val: `${rm.compliance_score_min != null ? rm.compliance_score_min.toFixed(0) : '—'}–${rm.compliance_score_max != null ? rm.compliance_score_max.toFixed(0) : '—'}`, fmt: v => v, bg: 'linear-gradient(135deg, #b71c1c 0%, #ef5350 100%)' },
              ].map(({ key, tipKey, label, val, fmt, bg }) => (
                <Grid item xs={6} sm={3} key={key}>
                  <Tooltip arrow title={METRIC_TOOLTIPS[tipKey] || ''}>
                    <Paper sx={{ background: bg, color: 'white', p: 1.5, cursor: 'help' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{label}</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.1 }}>{fmt(val)}</Typography>
                    </Paper>
                  </Tooltip>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Alert Classification (Paper 2 abstract F5) */}
        {rm.alert_classification && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="overline" color="secondary" sx={{ letterSpacing: 0.6, fontWeight: 700 }}>
                Alert Classification (severity tier per audit)
              </Typography>
              <Chip size="small" label="DB live" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Per-audit severity bucket from compliance score: Critical (&lt;30), High (30–49), Medium (50–79), Low (80–95), Compliant (≥96). Paper 2 abstract finding F5.
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {[
                { key: 'critical', tipKey: 'alert.critical', label: 'Critical', desc: 'Score < 30 — needs immediate review', bg: 'linear-gradient(135deg, #b71c1c 0%, #e57373 100%)' },
                { key: 'high', tipKey: 'alert.high', label: 'High', desc: 'Score 30–49 — significant gaps', bg: 'linear-gradient(135deg, #e65100 0%, #ffb74d 100%)' },
                { key: 'medium', tipKey: 'alert.medium', label: 'Medium', desc: 'Score 50–79 — partial compliance', bg: 'linear-gradient(135deg, #f9a825 0%, #fff176 100%)' },
                { key: 'low', tipKey: 'alert.low', label: 'Low', desc: 'Score 80–95 — minor findings', bg: 'linear-gradient(135deg, #388e3c 0%, #81c784 100%)' },
                { key: 'compliant', tipKey: 'alert.compliant', label: 'Compliant', desc: 'Score ≥ 96 — fully compliant', bg: 'linear-gradient(135deg, #1565c0 0%, #64b5f6 100%)' },
              ].map(({ key, tipKey, label, desc, bg }) => {
                const v = rm.alert_classification[key] || { n: 0, pct: 0 };
                return (
                  <Grid item xs={6} sm={4} md key={key}>
                    <Tooltip title={METRIC_TOOLTIPS[tipKey] || `${v.n} audits at ${label} severity. ${desc}`} arrow>
                      <Paper sx={{ background: bg, color: key === 'medium' ? 'rgba(0,0,0,0.85)' : 'white', boxShadow: 2, textAlign: 'center', p: 1.5, cursor: 'help' }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{v.n}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>{label} ({v.pct}%)</Typography>
                        <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.7, lineHeight: 1.15, mt: 0.25 }}>{desc}</Typography>
                      </Paper>
                    </Tooltip>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}

        {/* Cascade Tier Histogram (Paper 2 Table VI / Figure 2) */}
        {Array.isArray(rm.cascade_tier_histogram) && rm.cascade_tier_histogram.length > 0 && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="overline" color="secondary" sx={{ letterSpacing: 0.6, fontWeight: 700 }}>
                Score-Extraction Cascade Tier Distribution
              </Typography>
              <Chip size="small" label="DB live" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Per-tier resolution counts across instrumented audits. Paper 2 finding F4: the cascade is effectively bimodal — Tier 1 (clean JSON) and Tier 4 (severity deduction) dominate; intermediate tiers never fire.
            </Typography>
            <Paper sx={{ p: 1.5, mb: 3, boxShadow: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Tier</strong></TableCell>
                    <TableCell align="right"><strong>Audits</strong></TableCell>
                    <TableCell align="right"><strong>Share</strong></TableCell>
                    <TableCell sx={{ minWidth: 120 }}><strong>Distribution</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rm.cascade_tier_histogram.map((t) => (
                    <Tooltip key={t.tier} arrow title={METRIC_TOOLTIPS[`cascade.T${t.tier}`] || ''} placement="top-start">
                      <TableRow sx={{ cursor: 'help' }}>
                        <TableCell>{t.label}</TableCell>
                        <TableCell align="right">{t.count}</TableCell>
                        <TableCell align="right">{t.share_pct?.toFixed(1)}%</TableCell>
                        <TableCell>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, t.share_pct || 0)}
                            sx={{ height: 8, borderRadius: 1,
                              '& .MuiLinearProgress-bar': { backgroundColor: t.tier === 1 ? '#388e3c' : t.tier === 4 ? '#1565c0' : t.tier === 0 ? '#c62828' : '#9e9e9e' } }}
                          />
                        </TableCell>
                      </TableRow>
                    </Tooltip>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}

        {/* RAG Retrieval Health (Paper 2 §V, line 733-735) */}
        {rm.rag_retrieval_health && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="overline" color="secondary" sx={{ letterSpacing: 0.6, fontWeight: 700 }}>
                RAG Retrieval Health (hybrid H = {rm.rag_retrieval_health.weighting_semantic ?? 0.7}·S<sub>sem</sub> + {rm.rag_retrieval_health.weighting_keyword ?? 0.3}·S<sub>kw</sub>)
              </Typography>
              <Chip size="small" label="Paper 2 §V" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
              {(() => {
                const m = rm._meta || {};
                const c = cacheChip(m.rag_cache_status, m.rag_cache_mtime, {
                  coverage_label: m.rag_cache_audits_walked != null ? `${m.rag_cache_audits_walked} audits` : null,
                  audits_added_since: m.audits_added_since_rag_cache,
                  refresh_cmd: 'scripts/compute_rag_metrics.py',
                });
                return <Chip size="small" label={c.text} color={c.color} variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />;
              })()}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Retriever operational health: coverage above the {rm.rag_retrieval_health.confidence_cutoff ?? 0.30} confidence cutoff, hybrid-score quantiles on the top-1 candidate, and band distribution. Paper 2 finding: retriever coverage is essentially universal (99.96%) but the score distribution is tightly clustered mid-range (IQR width just {rm.rag_retrieval_health.hybrid_score_iqr_width ?? 0.094}), consistent with the plausible-but-not-clearly-right disagreement pattern in §V-A. Source: <code>/tmp/nscat_rag_metrics_cache.json</code>.
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {(() => {
                const rh = rm.rag_retrieval_health;
                return (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <Tooltip arrow title={METRIC_TOOLTIPS['rag.coverage']}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #1b5e20 0%, #66bb6a 100%)', color: 'white', p: 1.5, cursor: 'help' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Retrieval Coverage</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.1 }}>
                            {rh.coverage_pct != null ? rh.coverage_pct.toFixed(2) + '%' : '—'}
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', opacity: 0.9, mt: 0.5 }}>
                            {rh.rag_hits ?? '—'} / {rh.findings_total ?? '—'} findings above H ≥ {rh.confidence_cutoff ?? 0.30}
                          </Typography>
                          <Typography sx={{ fontSize: '0.65rem', opacity: 0.85, mt: 0.3 }}>
                            {rh.below_cutoff ?? 0} resolved to SC-07 keyword fallback
                          </Typography>
                        </Paper>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Tooltip arrow title={METRIC_TOOLTIPS['rag.median']}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #0d47a1 0%, #64b5f6 100%)', color: 'white', p: 1.5, cursor: 'help' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Hybrid Score Median</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.1 }}>
                            {rh.hybrid_score_median ?? '—'}
                          </Typography>
                          <Tooltip arrow title={METRIC_TOOLTIPS['rag.iqr']}>
                            <Typography sx={{ fontSize: '0.7rem', opacity: 0.9, mt: 0.5, cursor: 'help' }}>
                              IQR [{rh.hybrid_score_p25 ?? '—'}, {rh.hybrid_score_p75 ?? '—'}] · width {rh.hybrid_score_iqr_width ?? '—'}
                            </Typography>
                          </Tooltip>
                          <Tooltip arrow title={METRIC_TOOLTIPS['rag.min_max']}>
                            <Typography sx={{ fontSize: '0.65rem', opacity: 0.85, mt: 0.3, cursor: 'help' }}>
                              min {rh.hybrid_score_min ?? '—'} · max {rh.hybrid_score_max ?? '—'}
                            </Typography>
                          </Tooltip>
                        </Paper>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ background: 'linear-gradient(135deg, #4a148c 0%, #ab47bc 100%)', color: 'white', p: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Score Bands</Typography>
                        <Tooltip arrow title={METRIC_TOOLTIPS['rag.band_high']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>
                            High (H ≥ 0.515):     <strong>{rh.band_high_pct != null ? rh.band_high_pct.toFixed(1) + '%' : '—'}</strong>
                          </Typography>
                        </Tooltip>
                        <Tooltip arrow title={METRIC_TOOLTIPS['rag.band_medium']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>
                            Medium (0.418–0.515):  <strong>{rh.band_medium_pct != null ? rh.band_medium_pct.toFixed(1) + '%' : '—'}</strong>
                          </Typography>
                        </Tooltip>
                        <Tooltip arrow title={METRIC_TOOLTIPS['rag.band_low']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>
                            Low (H &lt; 0.418):     <strong>{rh.band_low_pct != null ? rh.band_low_pct.toFixed(1) + '%' : '—'}</strong>
                          </Typography>
                        </Tooltip>
                        <Tooltip arrow title={METRIC_TOOLTIPS['rag.band_below']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>
                            Below cutoff:        <strong>{rh.band_below_cutoff_pct != null ? rh.band_below_cutoff_pct.toFixed(2) + '%' : '—'}</strong>
                          </Typography>
                        </Tooltip>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ background: 'linear-gradient(135deg, #01579b 0%, #4fc3f7 100%)', color: 'white', p: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Retrieval Config</Typography>
                        <Tooltip arrow title={METRIC_TOOLTIPS['rag.weighting']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>Weighting: <strong>{rh.weighting_semantic}</strong>/<strong>{rh.weighting_keyword}</strong> sem/kw</Typography>
                        </Tooltip>
                        <Tooltip arrow title={METRIC_TOOLTIPS['rag.cutoff']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>Cutoff τ: <strong>{rh.confidence_cutoff}</strong></Typography>
                        </Tooltip>
                        <Tooltip arrow title={METRIC_TOOLTIPS['rag.topk']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>Top-k: <strong>{rh.top_k}</strong></Typography>
                        </Tooltip>
                        <Tooltip arrow title={METRIC_TOOLTIPS['rag.corpus']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>Corpus: <strong>1,014</strong> controls</Typography>
                        </Tooltip>
                      </Paper>
                    </Grid>
                  </>
                );
              })()}
            </Grid>
          </>
        )}

        {/* Three-Way NIST Mapping Strategy Comparison (Paper 2 Table IX) */}
        {rm.three_way_mapping && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="overline" color="secondary" sx={{ letterSpacing: 0.6, fontWeight: 700 }}>
                Three-Way NIST Mapping Strategy Comparison (n = {rm.three_way_mapping.n ?? 100})
              </Typography>
              {(() => {
                const m = rm._meta || {};
                const c = cacheChip(m.three_way_cache_status, m.three_way_mapping_mtime, {
                  coverage_label: m.three_way_gold_n != null ? `n=${m.three_way_gold_n}` : null,
                  refresh_cmd: 'scripts/compute_three_way_mapping.py',
                });
                return <Chip size="small" label={c.text} color={c.color} variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />;
              })()}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Top-1 NIST control-mapping accuracy on the 100-finding gold standard. Paper 2 finding F2: the simpler keyword strategy beats the production RAG retriever. Strict = c/n; Lenient = (c + 0.5p)/n. Source: <code>/tmp/nscat_three_way_mapping.json</code> (regenerate via <code>scripts/compute_three_way_mapping.py</code>).
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {[
                { key: 'keyword_categorical', tipKey: 'twm.kw', label: 'Keyword Categorical', desc: '43-pattern + 75-word stoplist + SC-07 fallback', bg: 'linear-gradient(135deg, #1b5e20 0%, #66bb6a 100%)' },
                { key: 'rag_hybrid', tipKey: 'twm.rag', label: 'RAG Hybrid', desc: '70/30 semantic+keyword, τ=0.30, top-k=5 (production)', bg: 'linear-gradient(135deg, #1565c0 0%, #4fc3f7 100%)' },
                { key: 'llm_native_citation', tipKey: 'twm.llm', label: 'LLM-Native', desc: 'Extract NIST IDs from LLM free-text Context/Recommendation', bg: 'linear-gradient(135deg, #6a1b9a 0%, #ba68c8 100%)' },
              ].map(({ key, tipKey, label, desc, bg }) => {
                const v = rm.three_way_mapping[key] || {};
                return (
                  <Grid item xs={12} sm={4} key={key}>
                    <Tooltip arrow title={METRIC_TOOLTIPS[tipKey] || desc}>
                      <Paper sx={{ background: bg, color: 'white', boxShadow: 2, p: 1.5, cursor: 'help' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{label}</Typography>
                        <Typography sx={{ fontSize: '0.65rem', opacity: 0.85, mb: 1, lineHeight: 1.2 }}>{desc}</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption">c={v.c ?? '—'} · p={v.p ?? '—'} · w={v.w ?? '—'}{v.none ? ` · ∅=${v.none}` : ''}</Typography>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.1 }}>
                          {v.strict_pct != null ? `${v.strict_pct.toFixed(1)}%` : '—'} <Typography component="span" sx={{ fontSize: '0.7rem', opacity: 0.85 }}>strict</Typography>
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.1 }}>
                          {v.lenient_pct != null ? `${v.lenient_pct.toFixed(1)}%` : '—'} <Typography component="span" sx={{ fontSize: '0.7rem', opacity: 0.85 }}>lenient</Typography>
                        </Typography>
                      </Paper>
                    </Tooltip>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}

        {/* Inter-Strategy Agreement (Paper 2 §V-A) */}
        {rm.inter_strategy_agreement && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="overline" color="secondary" sx={{ letterSpacing: 0.6, fontWeight: 700 }}>
                Inter-Strategy Agreement (n = {rm.inter_strategy_agreement.n_unique_findings ?? 2967} unique findings)
              </Typography>
              <Chip size="small" label="Paper 2 §V-A" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
              {(() => {
                const m = rm._meta || {};
                const c = cacheChip(m.inter_strategy_cache_status, m.inter_strategy_mtime, {
                  coverage_label: m.inter_strategy_audits_at_generation != null
                    ? `${m.inter_strategy_audits_at_generation} audits at gen`
                    : null,
                  audits_added_since: m.inter_strategy_audits_added_since,
                  refresh_cmd: 'scripts/compute_inter_strategy_agreement.py',
                });
                return <Chip size="small" label={c.text} color={c.color} variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />;
              })()}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Cross-method agreement over the full production corpus (no human-annotated ground truth). Krippendorff's α, Fleiss' κ, pairwise family + exact-control agreement, three-way breakdown, Shannon entropy. Source: <code>/tmp/nscat_inter_strategy_agreement.json</code> (regenerate via <code>scripts/compute_inter_strategy_agreement.py</code>).
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {(() => {
                const isa = rm.inter_strategy_agreement;
                const rel = isa.reliability || {};
                const cov = isa.coverage || {};
                const tw = isa.three_way_family_breakdown || {};
                const totN = isa.n_unique_findings || 2967;
                const pct = (n) => totN ? ((n / totN) * 100).toFixed(1) + '%' : '—';
                const H = isa.shannon_entropy_per_strategy_nats || {};
                return (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ background: 'linear-gradient(135deg, #4a148c 0%, #ab47bc 100%)', color: 'white', p: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Family Reliability</Typography>
                        <Tooltip arrow title={METRIC_TOOLTIPS['isa.krippendorff']}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.1, cursor: 'help' }}>
                            α = {rel.krippendorff_alpha_family_nominal != null ? rel.krippendorff_alpha_family_nominal.toFixed(3) : '—'}
                          </Typography>
                        </Tooltip>
                        <Tooltip arrow title={METRIC_TOOLTIPS['isa.fleiss']}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.1, cursor: 'help' }}>
                            κ = {rel.fleiss_kappa_family != null ? rel.fleiss_kappa_family.toFixed(3) : '—'}
                          </Typography>
                        </Tooltip>
                        <Typography sx={{ fontSize: '0.65rem', opacity: 0.85, mt: 0.5 }}>
                          Krippendorff / Fleiss (nominal, 3 raters). Slight-to-fair band (&lt;0.40).
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ background: 'linear-gradient(135deg, #b71c1c 0%, #ef5350 100%)', color: 'white', p: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Strategy Coverage</Typography>
                        <Tooltip arrow title={METRIC_TOOLTIPS['isa.s1_cov']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>
                            S1 keyword: <strong>{cov.s1_keyword != null ? (cov.s1_keyword * 100).toFixed(1) + '%' : '—'}</strong>
                          </Typography>
                        </Tooltip>
                        <Tooltip arrow title={METRIC_TOOLTIPS['isa.s2_cov']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>
                            S2 RAG:     <strong>{cov.s2_rag != null ? (cov.s2_rag * 100).toFixed(1) + '%' : '—'}</strong>
                          </Typography>
                        </Tooltip>
                        <Tooltip arrow title={METRIC_TOOLTIPS['isa.s3_cov']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>
                            S3 LLM:     <strong>{cov.s3_llm_native != null ? (cov.s3_llm_native * 100).toFixed(1) + '%' : '—'}</strong>{isa.s3_coverage_n ? ` (n=${isa.s3_coverage_n})` : ''}
                          </Typography>
                        </Tooltip>
                        <Typography sx={{ fontSize: '0.65rem', opacity: 0.85, mt: 0.5 }}>
                          S3 cannot stand alone.
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ background: 'linear-gradient(135deg, #01579b 0%, #4fc3f7 100%)', color: 'white', p: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Three-Way Breakdown</Typography>
                        <Tooltip arrow title={METRIC_TOOLTIPS['isa.tw_agree']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>
                            All agree: <strong>{tw.all_3_agree ?? '—'}</strong> ({pct(tw.all_3_agree ?? 0)})
                          </Typography>
                        </Tooltip>
                        <Tooltip arrow title={METRIC_TOOLTIPS['isa.tw_partial']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>
                            Partial:   <strong>{tw.exactly_2_agree ?? '—'}</strong> ({pct(tw.exactly_2_agree ?? 0)})
                          </Typography>
                        </Tooltip>
                        <Tooltip arrow title={METRIC_TOOLTIPS['isa.tw_disagree']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>
                            All disagree: <strong>{tw.all_3_disagree ?? '—'}</strong> ({pct(tw.all_3_disagree ?? 0)})
                          </Typography>
                        </Tooltip>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ background: 'linear-gradient(135deg, #1b5e20 0%, #66bb6a 100%)', color: 'white', p: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Family Diversity (H, nats)</Typography>
                        <Tooltip arrow title={METRIC_TOOLTIPS['isa.entropy_s1']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>
                            S1: <strong>{H.s1_keyword ?? '—'}</strong>
                          </Typography>
                        </Tooltip>
                        <Tooltip arrow title={METRIC_TOOLTIPS['isa.entropy_s2']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>
                            S2: <strong>{H.s2_rag ?? '—'}</strong>
                          </Typography>
                        </Tooltip>
                        <Tooltip arrow title={METRIC_TOOLTIPS['isa.entropy_s3']}>
                          <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, cursor: 'help' }}>
                            S3: <strong>{H.s3_llm_native ?? '—'}</strong>
                          </Typography>
                        </Tooltip>
                        {isa.s1_sc_concentration && (
                          <Tooltip arrow title={METRIC_TOOLTIPS['isa.sc_conc']}>
                            <Typography sx={{ fontSize: '0.65rem', opacity: 0.85, mt: 0.5, cursor: 'help' }}>
                              S1 {isa.s1_sc_concentration.family} = {isa.s1_sc_concentration.share_pct.toFixed(1)}%
                            </Typography>
                          </Tooltip>
                        )}
                      </Paper>
                    </Grid>
                  </>
                );
              })()}
            </Grid>
            <Paper sx={{ p: 1.5, mb: 3, boxShadow: 1, overflow: 'auto' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 700 }}>
                Pairwise agreement (family / exact control)
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Pair</strong></TableCell>
                    <TableCell align="right"><strong>Family agreement</strong></TableCell>
                    <TableCell align="right"><strong>Exact-control agreement</strong></TableCell>
                    <TableCell align="right"><strong>n</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { key: 's1_s2', tipKey: 'isa.pair_s1s2', label: 'S1 ↔ S2 (keyword vs RAG)' },
                    { key: 's1_s3', tipKey: 'isa.pair_s1s3', label: 'S1 ↔ S3 (keyword vs LLM-native)' },
                    { key: 's2_s3', tipKey: 'isa.pair_s2s3', label: 'S2 ↔ S3 (RAG vs LLM-native)' },
                  ].map(({ key, tipKey, label }) => {
                    const f = (rm.inter_strategy_agreement.pairwise_family_agreement || {})[key] || {};
                    const e = (rm.inter_strategy_agreement.pairwise_exact_agreement || {})[key] || {};
                    return (
                      <Tooltip key={key} arrow title={METRIC_TOOLTIPS[tipKey] || ''} placement="top-start">
                        <TableRow sx={{ cursor: 'help' }}>
                          <TableCell>{label}</TableCell>
                          <TableCell align="right">{f.agreement != null ? (f.agreement * 100).toFixed(1) + '%' : '—'}</TableCell>
                          <TableCell align="right">{e.agreement != null ? (e.agreement * 100).toFixed(1) + '%' : '—'}</TableCell>
                          <TableCell align="right">{f.n ?? e.n ?? '—'}</TableCell>
                        </TableRow>
                      </Tooltip>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}

        {/* Consensus Diagnostics (Paper 2 §IV-B) */}
        {rm.consensus_diagnostics && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="overline" color="secondary" sx={{ letterSpacing: 0.6, fontWeight: 700 }}>
                Consensus Diagnostics — ICC(2,1) &amp; Per-Mechanism Coverage
              </Typography>
              <Chip size="small" label="Paper 2 §IV-B" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
              {(() => {
                const m = rm._meta || {};
                const c = cacheChip(m.consensus_diagnostics_cache_status, m.consensus_diagnostics_mtime, {
                  coverage_label: m.consensus_diagnostics_audits_at_generation != null
                    ? `${m.consensus_diagnostics_audits_at_generation} audits at gen`
                    : null,
                  audits_added_since: m.consensus_diagnostics_audits_added_since,
                  refresh_cmd: 'scripts/compute_consensus_diagnostics.py',
                });
                return <Chip size="small" label={c.text} color={c.color} variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />;
              })()}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Two-way random absolute-agreement intraclass correlation on the first k=2 repeats per (firewall, ai_model) pair, plus per-mechanism coverage rates. Source: <code>/tmp/nscat_consensus_diagnostics.json</code>.
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {(() => {
                const cd = rm.consensus_diagnostics;
                return (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <Tooltip arrow title={METRIC_TOOLTIPS['cd.icc']}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #1b5e20 0%, #66bb6a 100%)', color: 'white', p: 1.5, cursor: 'help' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>ICC(2,1)</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.1 }}>
                            {cd.icc_value != null ? cd.icc_value.toFixed(3) : '—'}
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', opacity: 0.9, mt: 0.5, textTransform: 'capitalize' }}>
                            {cd.icc_interpretation ?? '—'} (n={cd.n_pairs_used ?? '—'} pairs, k=2)
                          </Typography>
                        </Paper>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Tooltip arrow title={METRIC_TOOLTIPS['cd.mech_a']}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #0d47a1 0%, #64b5f6 100%)', color: 'white', p: 1.5, cursor: 'help' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Mechanism A</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.1 }}>
                            {cd.mechanism_a_coverage_rate != null ? (cd.mechanism_a_coverage_rate * 100).toFixed(1) + '%' : '—'}
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', opacity: 0.9, mt: 0.5 }}>
                            Majority-vote coverage
                          </Typography>
                        </Paper>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Tooltip arrow title={METRIC_TOOLTIPS['cd.mech_b']}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #4a148c 0%, #ab47bc 100%)', color: 'white', p: 1.5, cursor: 'help' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Mechanism B</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.1 }}>
                            {cd.mechanism_b_coverage_rate != null ? (cd.mechanism_b_coverage_rate * 100).toFixed(1) + '%' : '—'}
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', opacity: 0.9, mt: 0.5 }}>
                            Tier-weighted mean coverage
                          </Typography>
                        </Paper>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Tooltip arrow title={METRIC_TOOLTIPS['cd.mech_c']}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #b71c1c 0%, #ef5350 100%)', color: 'white', p: 1.5, cursor: 'help' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Mechanism C</Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.1 }}>
                            {cd.mechanism_c_coverage_rate != null ? (cd.mechanism_c_coverage_rate * 100).toFixed(1) + '%' : '—'}
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', opacity: 0.9, mt: 0.5 }}>
                            Variance-threshold coverage
                          </Typography>
                        </Paper>
                      </Tooltip>
                    </Grid>
                  </>
                );
              })()}
            </Grid>
          </>
        )}

        {/* Failure-Mode Taxonomy (Paper 2 §V-B) */}
        {rm.failure_mode_taxonomy && rm.failure_mode_taxonomy.strict_subset && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="overline" color="secondary" sx={{ letterSpacing: 0.6, fontWeight: 700 }}>
                Failure-Mode Taxonomy — strict 3-way subset (n = {rm.failure_mode_taxonomy.strict_subset.n_cases ?? 81})
              </Typography>
              <Chip size="small" label="Paper 2 §V-B" color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
              {(() => {
                const m = rm._meta || {};
                const c = cacheChip(m.failure_mode_cache_status, m.failure_mode_mtime, {
                  coverage_label: m.failure_mode_audits_at_generation != null
                    ? `${m.failure_mode_audits_at_generation} audits at gen`
                    : null,
                });
                const tooltipText = m.failure_mode_cache_status === 'frozen'
                  ? 'Frozen artifact — qualitative LLM-classifier labels on the 81-case strict subset plus a 20-case adversarial blind review. Not an auto-refreshable statistic; a re-run requires a fresh classification pass and independent human review.'
                  : c.text;
                return (
                  <Tooltip title={tooltipText} arrow>
                    <Chip size="small" label={c.text} color={c.color} variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                  </Tooltip>
                );
              })()}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Per-category distribution on the 81 strict-3-way disagreement cases. Adversarial classifier agreement is a rule-application consistency check (20-case stratified sub-sample). Source: <code>/tmp/nscat_failure_mode_taxonomy.json</code>.
            </Typography>
            <Paper sx={{ p: 1.5, mb: 2, boxShadow: 1, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell align="right"><strong>Count</strong></TableCell>
                    <TableCell align="right"><strong>Share</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(rm.failure_mode_taxonomy.strict_subset.distribution || []).map((d) => {
                    const slug = d.category.toLowerCase().includes('domain bleed') ? 'fm.domain_bleed'
                              : d.category.toLowerCase().includes('fallback') ? 'fm.fallback'
                              : d.category.toLowerCase().includes('vocabulary') ? 'fm.vocab'
                              : d.category.toLowerCase().includes('family adjacency') ? 'fm.family_adj'
                              : d.category.toLowerCase().includes('vendor') ? 'fm.vendor'
                              : d.category.toLowerCase().includes('enhancement') ? 'fm.enhancement'
                              : null;
                    return (
                      <Tooltip key={d.category} arrow title={(slug && METRIC_TOOLTIPS[slug]) || d.description} placement="top-start">
                        <TableRow sx={{ cursor: 'help' }}>
                          <TableCell><strong>{d.category}</strong></TableCell>
                          <TableCell>{d.description}</TableCell>
                          <TableCell align="right">{d.count}</TableCell>
                          <TableCell align="right">{d.share_pct != null ? d.share_pct.toFixed(1) + '%' : '—'}</TableCell>
                        </TableRow>
                      </Tooltip>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
            {(() => {
              const strict = rm.failure_mode_taxonomy.strict_subset || {};
              const conf = strict.confidence_distribution || {};
              const adv = strict.adversarial_review || {};
              const loose = rm.failure_mode_taxonomy.loose_100_case_set || {};
              const looseGap = (loose.distribution || [])[0] || {};
              return (
                <Grid container spacing={1.5} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper sx={{ p: 1.5, boxShadow: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Classifier confidence</Typography>
                      <Tooltip arrow title={METRIC_TOOLTIPS['fm.conf_high']}>
                        <Typography sx={{ fontSize: '0.8rem', cursor: 'help' }}>High: <strong>{conf.high?.n ?? '—'}</strong> ({conf.high?.pct?.toFixed(1) ?? '—'}%)</Typography>
                      </Tooltip>
                      <Tooltip arrow title={METRIC_TOOLTIPS['fm.conf_med']}>
                        <Typography sx={{ fontSize: '0.8rem', cursor: 'help' }}>Medium: <strong>{conf.medium?.n ?? '—'}</strong> ({conf.medium?.pct?.toFixed(1) ?? '—'}%)</Typography>
                      </Tooltip>
                      <Tooltip arrow title={METRIC_TOOLTIPS['fm.conf_low']}>
                        <Typography sx={{ fontSize: '0.8rem', cursor: 'help' }}>Low: <strong>{conf.low?.n ?? '—'}</strong> ({conf.low?.pct?.toFixed(1) ?? '—'}%)</Typography>
                      </Tooltip>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Tooltip arrow title={METRIC_TOOLTIPS['fm.adversarial']}>
                      <Paper sx={{ p: 1.5, boxShadow: 1, cursor: 'help' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Adversarial review</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                          {adv.inter_classifier_agreement_pct != null ? adv.inter_classifier_agreement_pct.toFixed(1) + '%' : '—'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {adv.agree_count ?? '—'} / {adv.sample_size ?? '—'} agreement on blind stratified sub-sample
                        </Typography>
                      </Paper>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} sm={12} md={4}>
                    <Tooltip arrow title={METRIC_TOOLTIPS['fm.loose_100']}>
                      <Paper sx={{ p: 1.5, boxShadow: 1, cursor: 'help' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Looser 100-case set</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                          {looseGap.share_pct != null ? looseGap.share_pct.toFixed(1) + '%' : '—'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {looseGap.count ?? '—'} / {loose.n_cases ?? '—'} labelled <em>{looseGap.category ?? '—'}</em> (precedence rule)
                        </Typography>
                      </Paper>
                    </Tooltip>
                  </Grid>
                </Grid>
              );
            })()}
          </>
        )}

        {/* Per-Provider Performance (Paper 2 Tables IV, V, VII) */}
        {Array.isArray(rm.per_provider_stats) && rm.per_provider_stats.length > 0 && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="overline" color="secondary" sx={{ letterSpacing: 0.6, fontWeight: 700 }}>
                Per-Provider Performance &amp; Latency
              </Typography>
              <Chip size="small" label="DB live" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Compliance score distribution and audit duration per LLM provider. Paper 2 Tables IV (score), V (audit volume), VII (latency); finding F2: provider-choice signal spans a 56-point range.
            </Typography>
            <Paper sx={{ p: 1.5, mb: 3, boxShadow: 1, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.provider']}><TableCell sx={{ cursor: 'help' }}><strong>Provider</strong></TableCell></Tooltip>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.n']}><TableCell align="right" sx={{ cursor: 'help' }}><strong>n</strong></TableCell></Tooltip>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.mdl']}><TableCell align="right" sx={{ cursor: 'help' }}><strong>Mdl</strong></TableCell></Tooltip>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.mean']}><TableCell align="right" sx={{ cursor: 'help' }}><strong>Mean</strong></TableCell></Tooltip>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.med']}><TableCell align="right" sx={{ cursor: 'help' }}><strong>Med</strong></TableCell></Tooltip>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.sd']}><TableCell align="right" sx={{ cursor: 'help' }}><strong>SD</strong></TableCell></Tooltip>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.iqr']}><TableCell align="right" sx={{ cursor: 'help' }}><strong>IQR</strong></TableCell></Tooltip>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.p50s']}><TableCell align="right" sx={{ cursor: 'help' }}><strong>P50 s</strong></TableCell></Tooltip>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.p99s']}><TableCell align="right" sx={{ cursor: 'help' }}><strong>P99 s</strong></TableCell></Tooltip>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rm.per_provider_stats.map((p) => (
                    <TableRow key={p.provider}>
                      <TableCell>{p.provider}</TableCell>
                      <TableCell align="right">{p.audits}</TableCell>
                      <TableCell align="right">{p.models}</TableCell>
                      <TableCell align="right">{p.mean_score != null ? p.mean_score.toFixed(1) : '—'}</TableCell>
                      <TableCell align="right">{p.median_score != null ? p.median_score.toFixed(1) : '—'}</TableCell>
                      <TableCell align="right">{p.sd_score != null ? p.sd_score.toFixed(1) : '—'}</TableCell>
                      <TableCell align="right">{p.iqr_q1 != null && p.iqr_q3 != null ? `${p.iqr_q1.toFixed(0)}–${p.iqr_q3.toFixed(0)}` : '—'}</TableCell>
                      <TableCell align="right">{p.lat_p50_s != null ? p.lat_p50_s : '—'}</TableCell>
                      <TableCell align="right">{p.lat_p99_s != null ? p.lat_p99_s : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}

        {/* Per-Vendor Performance (Paper 2 Table XII) */}
        {Array.isArray(rm.per_vendor_stats) && rm.per_vendor_stats.length > 0 && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="overline" color="secondary" sx={{ letterSpacing: 0.6, fontWeight: 700 }}>
                Per-Vendor Performance &amp; Latency
              </Typography>
              <Chip size="small" label="DB live" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Compliance score distribution and audit duration per firewall vendor. Paper 2 Table XII; finding F5: Check Point and Huawei average 16.9 points below the other four. All six vendors have dedicated parsers, so this is not a parsing artefact.</Typography>
            <Paper sx={{ p: 1.5, mb: 3, boxShadow: 1, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.vendor']}><TableCell sx={{ cursor: 'help' }}><strong>Vendor</strong></TableCell></Tooltip>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.n']}><TableCell align="right" sx={{ cursor: 'help' }}><strong>n</strong></TableCell></Tooltip>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.mean']}><TableCell align="right" sx={{ cursor: 'help' }}><strong>Mean</strong></TableCell></Tooltip>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.med']}><TableCell align="right" sx={{ cursor: 'help' }}><strong>Med</strong></TableCell></Tooltip>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.sd']}><TableCell align="right" sx={{ cursor: 'help' }}><strong>SD</strong></TableCell></Tooltip>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.iqr']}><TableCell align="right" sx={{ cursor: 'help' }}><strong>IQR</strong></TableCell></Tooltip>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.p50s']}><TableCell align="right" sx={{ cursor: 'help' }}><strong>P50 s</strong></TableCell></Tooltip>
                    <Tooltip arrow title={METRIC_TOOLTIPS['col.p99s']}><TableCell align="right" sx={{ cursor: 'help' }}><strong>P99 s</strong></TableCell></Tooltip>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rm.per_vendor_stats.map((v) => (
                    <TableRow key={v.vendor_key}>
                      <TableCell>{v.vendor}</TableCell>
                      <TableCell align="right">{v.audits}</TableCell>
                      <TableCell align="right">{v.mean_score != null ? v.mean_score.toFixed(1) : '—'}</TableCell>
                      <TableCell align="right">{v.median_score != null ? v.median_score.toFixed(1) : '—'}</TableCell>
                      <TableCell align="right">{v.sd_score != null ? v.sd_score.toFixed(1) : '—'}</TableCell>
                      <TableCell align="right">{v.iqr_q1 != null && v.iqr_q3 != null ? `${v.iqr_q1.toFixed(0)}–${v.iqr_q3.toFixed(0)}` : '—'}</TableCell>
                      <TableCell align="right">{v.lat_p50_s != null ? v.lat_p50_s : '—'}</TableCell>
                      <TableCell align="right">{v.lat_p99_s != null ? v.lat_p99_s : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}

        {/* F5 Vendor Score Gap (Paper 2 §VI-A) */}
        {rm.parser_score_gap != null && rm.parser_avg_vendor != null && rm.parser_avg_generic != null && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="overline" color="secondary" sx={{ letterSpacing: 0.6, fontWeight: 700 }}>
                F5 Vendor Score Gap (Check Point + Huawei vs Other Four)
              </Typography>
              <Chip size="small" label="DB live" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Paper 2 §VI-A: Check Point and Huawei average 16.9 points below the other four. All six vendors have dedicated parsers with equivalent field coverage, so this is not a parsing artefact; the gap is carried by how often the score reaches its floor.</Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={4} md>
                <Tooltip title={`Cisco FTD + Palo Alto + Juniper SRX + Fortinet, n=${rm.parser_vendor_count}. Vendor-specific parsers populate 20/22 CDM entity fields.`} arrow>
                  <Paper sx={{ background: 'linear-gradient(135deg, #1b5e20 0%, #81c784 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{rm.parser_avg_vendor?.toFixed(1)}%</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Vendor-specific (n={rm.parser_vendor_count})</Typography>
                    <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.8, lineHeight: 1.15, mt: 0.25 }}>Cisco / PA / Juniper / Fortinet mean score</Typography>
                  </Paper>
                </Tooltip>
              </Grid>
              <Grid item xs={6} sm={4} md>
                <Tooltip title={`Huawei USG + Check Point Security Gateway, n=${rm.parser_generic_count}. These two platforms average 16.9 points below the other four. Both have dedicated parsers with equivalent field coverage, so the gap is not a parsing artefact.`} arrow>
                  <Paper sx={{ background: 'linear-gradient(135deg, #b71c1c 0%, #e57373 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{rm.parser_avg_generic?.toFixed(1)}%</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Check Point + Huawei (n={rm.parser_generic_count})</Typography>
                    <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.8, lineHeight: 1.15, mt: 0.25 }}>Huawei + Checkpoint mean score</Typography>
                  </Paper>
                </Tooltip>
              </Grid>
              <Grid item xs={6} sm={4} md>
                <Tooltip title="Vendor-specific minus generic-fallback. Paper 2 attributes most of this gap to parser-fidelity loss rather than configuration weakness." arrow>
                  <Paper sx={{ background: 'linear-gradient(135deg, #e65100 0%, #ffb74d 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{rm.parser_score_gap?.toFixed(1)} pp</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Depression Gap</Typography>
                    <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.8, lineHeight: 1.15, mt: 0.25 }}>Other four − Check Point/Huaweik</Typography>
                  </Paper>
                </Tooltip>
              </Grid>
            </Grid>
          </>
        )}

        {/* NIST Family Distribution (Paper 2 Table X) */}
        {Array.isArray(rm.nist_family_distribution) && rm.nist_family_distribution.length > 0 && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="overline" color="secondary" sx={{ letterSpacing: 0.6, fontWeight: 700 }}>
                NIST Control Family Distribution
              </Typography>
              {(() => {
                const m = rm._meta || {};
                const c = cacheChip(m.rag_cache_status, m.rag_cache_mtime, {
                  coverage_label: m.rag_cache_audits_walked != null
                    ? `${m.rag_cache_audits_walked} audits` : null,
                  audits_added_since: m.audits_added_since_rag_cache,
                  refresh_cmd: 'scripts/compute_rag_metrics.py',
                });
                return <Chip size="small" label={c.text} color={c.color} variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />;
              })()}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Top-1 NIST SP 800-53 control-family citations across all mapped findings. Paper 2 Table X; SC + SI + AC + IA dominate ~83% of citations. Source: <code>/tmp/nscat_rag_metrics_cache.json</code>.
            </Typography>
            <Paper sx={{ p: 1.5, mb: 3, boxShadow: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Family</strong></TableCell>
                    <TableCell align="right"><strong>Citations</strong></TableCell>
                    <TableCell align="right"><strong>Share</strong></TableCell>
                    <TableCell sx={{ minWidth: 120 }}><strong>Distribution</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rm.nist_family_distribution.slice(0, 10).map((f) => (
                    <TableRow key={f.family}>
                      <TableCell>{f.family}</TableCell>
                      <TableCell align="right">{f.citations}</TableCell>
                      <TableCell align="right">{f.share_pct?.toFixed(1)}%</TableCell>
                      <TableCell>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, f.share_pct || 0)}
                          sx={{ height: 8, borderRadius: 1,
                            '& .MuiLinearProgress-bar': { backgroundColor: '#1565c0' } }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}

        {/* RAG Family Accuracy (Paper 2 Table VIII) */}
        {rm.rag_family_accuracy && Array.isArray(rm.rag_family_accuracy.family_stats) && rm.rag_family_accuracy.family_stats.length > 0 && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="overline" color="secondary" sx={{ letterSpacing: 0.6, fontWeight: 700 }}>
                RAG-Hybrid Per-Family Top-1 Accuracy
              </Typography>
              {fmtTs((rm._meta || {}).ground_truth_mtime) && (
                <Chip
                  size="small"
                  label={`Annotation: ${fmtTs((rm._meta || {}).ground_truth_mtime)}`}
                  color="default"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 18 }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Per-family RAG-hybrid mapping accuracy against the 100-finding gold standard. Paper 2 Table VIII; SC family is best understood (~45% strict), CM/SI worst. Source: <code>FE-NSCAT/src/data/ragGroundTruth.json</code> (first-author annotation, 2026-05-03).
            </Typography>
            <Paper sx={{ p: 1.5, mb: 3, boxShadow: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Family</strong></TableCell>
                    <TableCell align="right"><strong>n</strong></TableCell>
                    <TableCell align="right"><strong>c</strong></TableCell>
                    <TableCell align="right"><strong>p</strong></TableCell>
                    <TableCell align="right"><strong>w</strong></TableCell>
                    <TableCell align="right"><strong>Strict</strong></TableCell>
                    <TableCell align="right"><strong>Lenient</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rm.rag_family_accuracy.family_stats.map((f) => (
                    <TableRow key={f.family}>
                      <TableCell>{f.family}</TableCell>
                      <TableCell align="right">{f.n}</TableCell>
                      <TableCell align="right">{f.c}</TableCell>
                      <TableCell align="right">{f.p}</TableCell>
                      <TableCell align="right">{f.w}</TableCell>
                      <TableCell align="right">{f.strict_pct?.toFixed(1)}%</TableCell>
                      <TableCell align="right">{f.lenient_pct?.toFixed(1)}%</TableCell>
                    </TableRow>
                  ))}
                  {rm.rag_family_accuracy.headline && (
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell><strong>All families</strong></TableCell>
                      <TableCell align="right"><strong>{rm.rag_family_accuracy.headline.n}</strong></TableCell>
                      <TableCell align="right"><strong>{rm.rag_family_accuracy.headline.c}</strong></TableCell>
                      <TableCell align="right"><strong>{rm.rag_family_accuracy.headline.p}</strong></TableCell>
                      <TableCell align="right"><strong>{rm.rag_family_accuracy.headline.w}</strong></TableCell>
                      <TableCell align="right"><strong>{rm.rag_family_accuracy.headline.strict_pct?.toFixed(1)}%</strong></TableCell>
                      <TableCell align="right"><strong>{rm.rag_family_accuracy.headline.lenient_pct?.toFixed(1)}%</strong></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}

        {/* Case Study (Paper 2 Figure 3 — audit_history.id=871 walkthrough) */}
        {rm.case_study_audit && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="overline" color="secondary" sx={{ letterSpacing: 0.6, fontWeight: 700 }}>
                End-to-End Case Study: Audit #{rm.case_study_audit.audit_id}
              </Typography>
              <Chip size="small" label="DB live" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Single-audit walkthrough used in Paper 2 Figure 3. Pulls every stored field for the referenced audit so reviewers can verify the figure matches live data.
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={4} md>
                <Tooltip title={`Device: ${rm.case_study_audit.device_name || '—'}, Vendor type: ${rm.case_study_audit.vendor || '—'}`} arrow>
                  <Paper sx={{ background: 'linear-gradient(135deg, #1565c0 0%, #4fc3f7 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.1, fontSize: '0.9rem' }}>{rm.case_study_audit.device_name || '—'}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Device</Typography>
                    <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.8, lineHeight: 1.15, mt: 0.25 }}>Firewall under audit</Typography>
                  </Paper>
                </Tooltip>
              </Grid>
              <Grid item xs={6} sm={4} md>
                <Tooltip title={`LLM that produced the audit. Full ai_model identifier: ${rm.case_study_audit.ai_model || '—'}`} arrow>
                  <Paper sx={{ background: 'linear-gradient(135deg, #4a148c 0%, #ba68c8 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.1, fontSize: '0.8rem' }}>{rm.case_study_audit.ai_model?.split(':').pop() || '—'}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>AI Model</Typography>
                    <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.8, lineHeight: 1.15, mt: 0.25 }}>LLM provider:model</Typography>
                  </Paper>
                </Tooltip>
              </Grid>
              <Grid item xs={6} sm={4} md>
                <Paper sx={{ background: 'linear-gradient(135deg, #00695c 0%, #4db6ac 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{rm.case_study_audit.score != null ? `${rm.case_study_audit.score.toFixed(0)}%` : '—'}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>Compliance Score</Typography>
                  <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.8, lineHeight: 1.15, mt: 0.25 }}>0–100, severity-weighted</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={4} md>
                <Paper sx={{ background: 'linear-gradient(135deg, #e65100 0%, #ffb74d 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{rm.case_study_audit.findings ?? '—'}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>Findings</Typography>
                  <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.8, lineHeight: 1.15, mt: 0.25 }}>Total observations surfaced</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={4} md>
                <Paper sx={{ background: 'linear-gradient(135deg, #283593 0%, #7986cb 100%)', color: 'white', boxShadow: 2, textAlign: 'center', p: 1.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{rm.case_study_audit.duration_s != null ? `${rm.case_study_audit.duration_s}s` : '—'}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>Duration</Typography>
                  <Typography sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.8, lineHeight: 1.15, mt: 0.25 }}>End-to-end wall-clock</Typography>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Collapse>

      <Grid container spacing={3}>


        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Recent Audit Reports
              </Typography>
              <Chip
                label={`${lastFiveAudits.length} reports`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            {lastFiveAudits.length > 0 ? (
              lastFiveAudits.map((audit, index) => {
                const reportData = auditReports[audit.id];
                const hasReport = reportData && reportData.length > 0;
                const score = audit.overall_score || 0;
                const scoreColor = score >= 80 ? '#388e3c' : score >= 50 ? '#fbc02d' : score >= 30 ? '#f57c00' : '#d32f2f';
                return (
                  <Box
                    key={audit.id || index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 1.5,
                      borderBottom: index < lastFiveAudits.length - 1 ? 1 : 0,
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: `${scoreColor}1F`,
                        borderRadius: '50%',
                        p: 1,
                        mr: 2,
                        minWidth: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AssessmentIcon sx={{ color: scoreColor }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                        {audit.firewall?.device_name || 'Unknown Device'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Score: {Math.round(score)}% — {formatLocalDateTime(audit.audit_date)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={`${Math.round(score)}%`}
                        size="small"
                        sx={{ backgroundColor: scoreColor, color: '#fff', fontWeight: 'bold', fontSize: '0.75rem', minWidth: 46 }}
                      />
                      {hasReport ? (
                        <>
                          <Tooltip title="Download Report">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadReport(reportData[0].id, reportData[0].filename)}
                              sx={{ color: scoreColor }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Report">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const report = reportData[0];
                                fetch(`${process.env.REACT_APP_API_URL}/audit-reports/${report.id}/download`, {
                                  headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
                                })
                                  .then(res => res.ok ? res.blob() : Promise.reject())
                                  .then(blob => {
                                    const url = window.URL.createObjectURL(blob);
                                    window.open(url, '_blank');
                                    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                                  })
                                  .catch(() => navigate('/history'));
                              }}
                              sx={{ color: scoreColor }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="View in Audit History">
                          <IconButton
                            size="small"
                            onClick={() => navigate('/history')}
                            sx={{ color: 'text.secondary' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                );
              })
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <AssessmentIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.5, mb: 1 }} />
                <Typography color="text.secondary">No audit reports available</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Critical SOC Reports Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 'fit-content' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Recent Critical Reports
              </Typography>
              <Chip
                label="Score &lt; 30"
                size="small"
                sx={{ backgroundColor: '#d32f2f', color: '#fff', fontWeight: 500, fontSize: '0.7rem' }}
              />
            </Box>
            {(() => {
              const criticalAudits = (auditHistory || [])
                .filter(a => a.overall_score !== null && a.overall_score !== undefined && Number(a.overall_score) < 30)
                .slice(0, 5);
              if (criticalAudits.length === 0) {
                return (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CheckCircleIcon sx={{ fontSize: 40, color: '#2e7d32', opacity: 0.5, mb: 1 }} />
                    <Typography color="text.secondary">No critical alerts — all audits above 30%</Typography>
                  </Box>
                );
              }
              return criticalAudits.map((audit, index) => {
                const reportData = auditReports[audit.id];
                const hasReport = reportData && reportData.length > 0;
                return (
                  <Box
                    key={audit.id || index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 1.5,
                      borderBottom: index < criticalAudits.length - 1 ? 1 : 0,
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: 'rgba(211, 47, 47, 0.12)',
                        borderRadius: '50%',
                        p: 1,
                        mr: 2,
                        minWidth: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ErrorIcon sx={{ color: '#d32f2f' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                        {audit.firewall?.device_name || 'Unknown Device'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Score: {Math.round(audit.overall_score)}% — {formatLocalDateTime(audit.audit_date)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={`${Math.round(audit.overall_score)}%`}
                        size="small"
                        sx={{ backgroundColor: '#d32f2f', color: '#fff', fontWeight: 'bold', fontSize: '0.75rem', minWidth: 46 }}
                      />
                      {hasReport ? (
                        <>
                          <Tooltip title="Download Report">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadReport(reportData[0].id, reportData[0].filename)}
                              sx={{ color: '#d32f2f' }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Report">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const report = reportData[0];
                                fetch(`${process.env.REACT_APP_API_URL}/audit-reports/${report.id}/download`, {
                                  headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
                                })
                                  .then(res => res.ok ? res.blob() : Promise.reject())
                                  .then(blob => {
                                    const url = window.URL.createObjectURL(blob);
                                    window.open(url, '_blank');
                                    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                                  })
                                  .catch(() => navigate('/history'));
                              }}
                              sx={{ color: '#d32f2f' }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="View in Audit History">
                          <IconButton
                            size="small"
                            onClick={() => navigate('/history')}
                            sx={{ color: 'text.secondary' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                );
              });
            })()}
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={2500}
        onClose={() => setCopySnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setCopySnackbar({ open: false, message: '' })}
          severity={copySnackbar.message.startsWith('Copy failed') ? 'error' : 'success'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {copySnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
